import { Editor } from "@tiptap/react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import FileSaver from "file-saver";
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    AlignmentType,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    ImageRun,
    PageBreak,
    Header,
    Footer,
    PageNumber,
    NumberFormat,
    TableOfContents,
    PageOrientation
} from "docx";
import { marked } from "marked";
import mermaid from "mermaid";
import { exportToSvg } from "@excalidraw/excalidraw";
import { parseExcalidrawBlocks } from "../extensions/excalidraw";

interface SectionData {
    section_title: string;
    content: string; // markdown content
}

/**
 * Export TipTap editor content to PDF with US Letter size, TOC, and headers/footers
 *
 * Features:
 * - US Letter size (8.5" × 11") with 0.75-1" margins
 * - Auto-generated Table of Contents from H1-H3 headers with page numbers
 * - Headers and footers with Work Product Name and page numbers
 * - Smart pagination to prevent splitting tables, diagrams, and code blocks
 * - Preserved heading hierarchy and layout fidelity
 *
 * Note: Uses html2canvas for rendering which converts content to raster images.
 * This ensures perfect visual fidelity but means diagrams are rasterized at 2x scale.
 * For true vector-based SVG diagrams, consider using the @react-pdf/renderer
 * implementation in exportPdf.tsx instead.
 */
export async function exportToPDF(editor: Editor, filename: string = "document.pdf", sectionsData?: SectionData[], workProductTitle?: string) {
    try {
        if (sectionsData && sectionsData.length > 0) {
            console.log(`[PDF Export] Exporting ${sectionsData.length} sections with proper pagination`);

            // PDF dimensions - US Letter size with proper margins (0.75 inch = 19.05mm)
            const pageWidth = 215.9; // 8.5 inches in mm
            const pageHeight = 279.4; // 11 inches in mm
            const topMargin = 20; // ~0.79 inches
            const bottomMargin = 20; // ~0.79 inches (includes space for footer)
            const sideMargin = 20; // ~0.79 inches
            const headerHeight = 8; // Space for header
            const footerHeight = 8; // Space for footer
            const contentWidth = pageWidth - 2 * sideMargin;
            const availableHeight = pageHeight - topMargin - bottomMargin - headerHeight - footerHeight;

            // More precise mm to px conversion at 96 DPI
            const mmToPxRatio = 3.7795275591;
            const containerWidthPx = Math.floor(contentWidth * mmToPxRatio);

            // Create a single container for ALL sections
            const exportContainer = document.createElement("div");
            exportContainer.id = "pdf-export-container";
            exportContainer.style.position = "absolute";
            exportContainer.style.left = "-9999px";
            exportContainer.style.top = "0";
            exportContainer.style.width = `${containerWidthPx}px`;
            exportContainer.style.maxWidth = `${containerWidthPx}px`;
            exportContainer.style.padding = "20px";
            exportContainer.style.margin = "0";
            exportContainer.style.backgroundColor = "#ffffff";
            exportContainer.style.fontFamily = "Arial, sans-serif";
            exportContainer.style.fontSize = "14px";
            exportContainer.style.lineHeight = "1.45";
            exportContainer.style.color = "#000000";
            exportContainer.style.boxSizing = "border-box";
            exportContainer.style.display = "block";
            exportContainer.style.overflow = "visible";
            exportContainer.style.height = "auto";
            exportContainer.style.wordWrap = "break-word";

            // Add comprehensive CSS styling
            const styleElement = document.createElement("style");
            styleElement.textContent = `
                #pdf-export-container * { 
                    box-sizing: border-box;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                #pdf-export-container .section-wrapper {
                    page-break-inside: avoid;
                    break-inside: avoid;
                    margin-bottom: 30px;
                    display: block;
                }
                #pdf-export-container table { 
                    border-collapse: collapse; 
                    width: 100% !important;
                    max-width: 100% !important;
                    margin: 10px 0; 
                    border: 1px solid #000;
                    table-layout: fixed !important;
                    page-break-inside: avoid;
                    break-inside: avoid;
                    box-sizing: border-box !important;
                }
                #pdf-export-container th, #pdf-export-container td { 
                    border: 1px solid #000; 
                    padding: 5px 4px;
                    text-align: left;
                    white-space: normal !important;
                    word-break: break-word !important;
                    overflow-wrap: break-word !important;
                    word-wrap: break-word !important;
                    font-size: 10px;
                    line-height: 1.2;
                    overflow: hidden;
                    box-sizing: border-box !important;
                    max-width: 0;
                }
                #pdf-export-container td p,
                #pdf-export-container th p {
                    margin: 0 0 4px 0;
                    line-height: 1.2;
                }
                #pdf-export-container td p:last-child,
                #pdf-export-container th p:last-child {
                    margin-bottom: 0;
                }
                #pdf-export-container td ul,
                #pdf-export-container td ol,
                #pdf-export-container th ul,
                #pdf-export-container th ol {
                    margin: 2px 0 4px 0;
                    padding-left: 18px;
                }
                #pdf-export-container td li,
                #pdf-export-container th li {
                    margin: 1px 0;
                    line-height: 1.2;
                    padding-left: 2px;
                }
                #pdf-export-container th { 
                    background-color: #f0f0f0; 
                    font-weight: bold;
                    font-size: 11px;
                }
                #pdf-export-container h1, 
                #pdf-export-container h2, 
                #pdf-export-container h3, 
                #pdf-export-container h4, 
                #pdf-export-container h5, 
                #pdf-export-container h6 { 
                    font-weight: bold !important; 
                    margin-top: 20px; 
                    margin-bottom: 10px;
                    word-wrap: break-word;
                    page-break-after: avoid;
                    break-after: avoid;
                }
                #pdf-export-container h1 { font-size: 24px; }
                #pdf-export-container h2 { font-size: 20px; }
                #pdf-export-container h3 { font-size: 18px; }
                #pdf-export-container h4 { font-size: 16px; }
                #pdf-export-container h5 { font-size: 14px; }
                #pdf-export-container h6 { font-size: 12px; }
                #pdf-export-container strong { 
                    font-weight: bold !important; 
                }
                #pdf-export-container p {
                    margin: 0 0 8px 0;
                    line-height: 1.45;
                    white-space: pre-wrap;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    word-break: break-word;
                    max-width: 100%;
                    font-size: 14px;
                }
                #pdf-export-container ul, 
                #pdf-export-container ol { 
                    margin: 0 0 8px 0; 
                    padding-left: 25px;
                    list-style-position: outside;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                #pdf-export-container ul { 
                    list-style-type: disc; 
                }
                #pdf-export-container ol { 
                    list-style-type: decimal; 
                }
                #pdf-export-container li { 
                    margin: 2px 0;
                    padding-left: 5px;
                    line-height: 1.45;
                    display: list-item;
                    word-wrap: break-word;
                    page-break-inside: avoid;
                    break-inside: avoid;
                    orphans: 3;
                    widows: 3;
                }
                #pdf-export-container ul ul, 
                #pdf-export-container ol ul { 
                    list-style-type: circle; 
                    margin: 4px 0;
                    padding-left: 25px;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                #pdf-export-container ul ol, 
                #pdf-export-container ol ol { 
                    list-style-type: lower-alpha; 
                    margin: 4px 0;
                    padding-left: 25px;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                #pdf-export-container br {
                    display: block;
                    content: "";
                    margin: 8px 0;
                }
                #pdf-export-container .excalidraw-container,
                #pdf-export-container [data-excalidraw] {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                    page-break-before: auto;
                    page-break-after: auto;
                    margin: 16px 0;
                    display: block;
                }
            `;
            exportContainer.appendChild(styleElement);

            // Extract headings for TOC (before adding to container)
            interface TocEntry {
                title: string;
                level: number;
                yPosition: number;
                pageNumber?: number;
            }
            const tocEntries: TocEntry[] = [];

            // Helper function to clean text directly (for strings)
            const cleanTextDirect = (text: string): string => {
                console.log(`[PDF Export TOC] Original text: "${text}"`);

                // Remove ALL emoji and special characters using a comprehensive pattern
                // This uses a single regex that matches all emoji-related code points
                let cleaned = text.replace(
                    /[\p{Emoji}\p{Emoji_Component}\p{Emoji_Modifier}\p{Emoji_Modifier_Base}\p{Emoji_Presentation}\u{FE00}-\u{FE0F}\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2B50}]/gu,
                    ""
                );

                // Remove HTML entities and artifacts
                cleaned = cleaned.replace(/&[a-zA-Z0-9#]+;/g, ""); // Remove all HTML entities
                cleaned = cleaned.replace(/&/g, ""); // Remove any remaining ampersands

                // Remove special punctuation marks that might be emoji-related
                cleaned = cleaned.replace(/[\u{2030}-\u{205F}]/gu, "");

                // Clean up whitespace and special characters
                cleaned = cleaned
                    .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width characters
                    .replace(/[‑]/g, "-") // Replace special hyphens with regular ones
                    .replace(/\s+/g, " ") // Replace multiple spaces with single space
                    .trim();

                console.log(`[PDF Export TOC] Cleaned text: "${cleaned}"`);

                return cleaned;
            };

            // Helper function to clean heading text from DOM elements
            const cleanHeadingText = (element: HTMLElement): string => {
                // Get raw text using textContent
                const text = element.textContent || "";
                return cleanTextDirect(text);
            };

            // Add document title if provided
            if (workProductTitle) {
                const titleElement = document.createElement("h1");
                titleElement.textContent = workProductTitle;
                titleElement.style.fontSize = "28px";
                titleElement.style.fontWeight = "900";
                titleElement.style.fontFamily = "Arial, Helvetica, sans-serif";
                titleElement.style.marginBottom = "30px";
                titleElement.style.marginTop = "0";
                titleElement.style.color = "#000000";
                exportContainer.appendChild(titleElement);
            }

            // Process and add ALL sections to the container
            for (let i = 0; i < sectionsData.length; i++) {
                const section = sectionsData[i];

                // Create section wrapper for boundary detection only.
                // Do not force section-level page breaks - pagination should fill each
                // page to the bottom and only break for protected content boundaries.
                const sectionWrapper = document.createElement("div");
                sectionWrapper.className = "section-wrapper";
                sectionWrapper.setAttribute("data-section-index", i.toString());

                // Section title removed from PDF content per user request
                // (Section titles are no longer displayed in the PDF)

                // Parse and add section content
                // First, parse Excalidraw blocks (convert <!-- EXCALIDRAW_START --> markers to HTML)
                let markdownContent = parseExcalidrawBlocks(section.content || "");

                // Log how many Excalidraw blocks were found in this section
                const excalidrawCount = (markdownContent.match(/data-excalidraw/g) || []).length;
                if (excalidrawCount > 0) {
                    console.log(`[PDF Export] Section "${section.section_title}" has ${excalidrawCount} Excalidraw block(s) after parsing`);
                }

                // Then, process Mermaid diagrams BEFORE converting markdown to HTML
                // Store rendered Mermaid SVGs with placeholders
                const mermaidDiagrams: Map<string, { svg: string; code: string }> = new Map();
                const mermaidRegex = /```mermaid\s+([\s\S]*?)```/g;
                let match;
                const mermaidReplacements: Array<{ original: string; replacement: string }> = [];

                while ((match = mermaidRegex.exec(markdownContent)) !== null) {
                    const fullMatch = match[0];
                    const mermaidCode = match[1].trim();

                    try {
                        console.log(`[PDF Export] Processing Mermaid diagram in section "${section.section_title}"`);
                        mermaid.initialize({ startOnLoad: false, theme: "default" });
                        const mermaidId = `mermaid-pdf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                        const { svg } = await mermaid.render(mermaidId, mermaidCode);

                        // Store the diagram data with a unique placeholder
                        const placeholderId = `MERMAID_PLACEHOLDER_${mermaidId}`;
                        mermaidDiagrams.set(placeholderId, { svg, code: mermaidCode });

                        // Use a unique marker that won't be escaped by marked.parse
                        const replacement = `\n\n<!-- ${placeholderId} -->\n\n`;
                        mermaidReplacements.push({ original: fullMatch, replacement });
                        console.log(`[PDF Export] Successfully rendered Mermaid diagram with placeholder ${placeholderId}`);
                    } catch (error) {
                        console.error("[PDF Export] Failed to render Mermaid diagram:", error);
                        mermaidReplacements.push({
                            original: fullMatch,
                            replacement: "\n\n[Mermaid Diagram - Rendering Error]\n\n"
                        });
                    }
                }

                // Apply all Mermaid replacements to the markdown
                for (const { original, replacement } of mermaidReplacements) {
                    markdownContent = markdownContent.replace(original, replacement);
                }

                // Now parse markdown to HTML
                let htmlContent = await marked.parse(markdownContent);

                // Replace placeholders with actual div elements after markdown processing
                for (const [placeholderId, { svg, code }] of mermaidDiagrams.entries()) {
                    const divHtml = `<div data-mermaid="true" data-code="${code.replace(/"/g, "&quot;")}" data-svg="${encodeURIComponent(svg)}"></div>`;
                    htmlContent = htmlContent.replace(`<!-- ${placeholderId} -->`, divHtml);
                }

                const contentDiv = document.createElement("div");
                contentDiv.innerHTML = htmlContent;
                contentDiv.style.width = "100%";
                contentDiv.style.maxWidth = "100%";
                contentDiv.style.boxSizing = "border-box";

                // Apply table wrapping styles and force proper sizing
                const tables = contentDiv.querySelectorAll("table");
                tables.forEach(table => {
                    const htmlTable = table as HTMLElement;

                    // Remove any inline width styles that might interfere
                    htmlTable.removeAttribute("width");
                    htmlTable.style.width = "100%";
                    htmlTable.style.maxWidth = "100%";
                    htmlTable.style.tableLayout = "fixed";
                    htmlTable.style.boxSizing = "border-box";

                    // Process table cells
                    const cells = htmlTable.querySelectorAll("th, td");
                    cells.forEach(cell => {
                        const htmlCell = cell as HTMLElement;

                        // Remove any inline width styles
                        htmlCell.removeAttribute("width");
                        htmlCell.style.width = "";
                        htmlCell.style.maxWidth = "none";
                        htmlCell.style.minWidth = "0";

                        // Force text wrapping
                        htmlCell.style.whiteSpace = "normal";
                        htmlCell.style.wordBreak = "break-word";
                        htmlCell.style.overflowWrap = "break-word";
                        htmlCell.style.wordWrap = "break-word";
                        htmlCell.style.overflow = "hidden";
                        htmlCell.style.boxSizing = "border-box";
                    });
                });

                // Process Mermaid diagrams (use pre-rendered SVGs)
                const mermaidElements = contentDiv.querySelectorAll("[data-mermaid]");
                console.log(`[PDF Export] Found ${mermaidElements.length} Mermaid diagram(s)`);

                for (const mermaidEl of Array.from(mermaidElements)) {
                    try {
                        // Check if we have pre-rendered SVG data
                        const svgData = mermaidEl.getAttribute("data-svg");
                        let svg: string;

                        if (svgData) {
                            // Use pre-rendered SVG
                            svg = decodeURIComponent(svgData);
                            console.log("[PDF Export] Using pre-rendered Mermaid SVG");
                        } else {
                            // Fall back to rendering from code (for backward compatibility)
                            const code = mermaidEl.getAttribute("data-code");
                            if (!code) {
                                throw new Error("No Mermaid code or SVG data found");
                            }
                            console.log("[PDF Export] Rendering Mermaid from code (fallback)");
                            mermaid.initialize({ startOnLoad: false, theme: "default" });
                            const mermaidId = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                            const result = await mermaid.render(mermaidId, code);
                            svg = result.svg;
                        }

                        const svgContainer = document.createElement("div");
                        svgContainer.innerHTML = svg;
                        svgContainer.style.textAlign = mermaidEl.getAttribute("data-align") || "left";
                        svgContainer.style.margin = "10px 0";
                        svgContainer.style.maxWidth = "100%";
                        mermaidEl.replaceWith(svgContainer);
                        console.log("[PDF Export] Successfully rendered Mermaid diagram");
                    } catch (error) {
                        console.error("Failed to render Mermaid diagram for PDF:", error);
                        const placeholder = document.createElement("div");
                        placeholder.textContent = "[Mermaid Diagram - Rendering Error]";
                        placeholder.style.padding = "20px";
                        placeholder.style.border = "1px dashed #ccc";
                        placeholder.style.margin = "10px 0";
                        mermaidEl.replaceWith(placeholder);
                    }
                }

                // Process Excalidraw diagrams
                const excalidrawElements = contentDiv.querySelectorAll("[data-excalidraw]");
                console.log(`[PDF Export] Found ${excalidrawElements.length} Excalidraw diagram(s)`);

                for (const excalidrawEl of Array.from(excalidrawElements)) {
                    const dataAttr = excalidrawEl.getAttribute("data-excalidraw-data");
                    if (dataAttr) {
                        try {
                            // Decode HTML entities from the attribute
                            const decodedData = dataAttr
                                .replace(/&amp;/g, "&")
                                .replace(/&quot;/g, '"')
                                .replace(/&#39;/g, "'")
                                .replace(/&lt;/g, "<")
                                .replace(/&gt;/g, ">");

                            // Parse Excalidraw JSON data
                            const excalidrawData = JSON.parse(decodedData);
                            console.log(`[PDF Export] Rendering Excalidraw diagram with ${excalidrawData.elements?.length || 0} elements`);

                            // Render Excalidraw to SVG
                            const svg = await exportToSvg({
                                elements: excalidrawData.elements || [],
                                appState: {
                                    ...excalidrawData.appState,
                                    exportWithDarkMode: false
                                },
                                files: excalidrawData.files || {}
                            });

                            // Get dimensions from the SVG's viewBox (which Excalidraw sets correctly)
                            // The viewBox format is: "x y width height"
                            const viewBox = svg.getAttribute("viewBox");
                            let svgWidth = 1200; // default fallback
                            let svgHeight = 700; // default fallback

                            if (viewBox) {
                                const [, , width, height] = viewBox.split(" ").map(Number);
                                if (width && height) {
                                    svgWidth = Math.ceil(width);
                                    svgHeight = Math.ceil(height);
                                    console.log(`[PDF Export] Using Excalidraw's viewBox dimensions: ${svgWidth}x${svgHeight}`);
                                }
                            } else {
                                console.warn(`[PDF Export] No viewBox found, using defaults: ${svgWidth}x${svgHeight}`);
                            }

                            console.log(`[PDF Export] SVG dimensions: ${svgWidth}x${svgHeight}`);

                            // Calculate max width available on the PDF page (accounting for margins)
                            // exportContainer width is typically 1200px
                            const maxContainerWidth = 1200; // Match the export container width

                            // Scale SVG to fit within container if necessary
                            if (svgWidth > maxContainerWidth) {
                                const scale = maxContainerWidth / svgWidth;
                                svgWidth = maxContainerWidth;
                                svgHeight = svgHeight * scale;
                                console.log(`[PDF Export] Scaled SVG to fit: ${svgWidth}x${svgHeight}`);
                            }

                            // Set explicit dimensions on the SVG
                            svg.setAttribute("width", `${svgWidth}px`);
                            svg.setAttribute("height", `${svgHeight}px`);
                            svg.style.width = `${svgWidth}px`;
                            svg.style.height = `${svgHeight}px`;
                            svg.style.display = "block";

                            // Create container for SVG
                            const svgContainer = document.createElement("div");
                            svgContainer.appendChild(svg);
                            svgContainer.style.textAlign = excalidrawEl.getAttribute("data-align") || "left";
                            svgContainer.style.margin = "10px 0";
                            svgContainer.style.maxWidth = "100%";
                            svgContainer.style.overflow = "visible";
                            svgContainer.style.width = `${svgWidth}px`;
                            svgContainer.style.height = `${svgHeight}px`;

                            // Replace Excalidraw element with rendered SVG
                            excalidrawEl.replaceWith(svgContainer);
                            console.log(`[PDF Export] Successfully rendered Excalidraw diagram at ${svgWidth}x${svgHeight}`);
                        } catch (error) {
                            console.error("Failed to render Excalidraw diagram for PDF:", error);
                            const placeholder = document.createElement("div");
                            placeholder.textContent = "[Excalidraw Diagram - Rendering Error]";
                            placeholder.style.padding = "20px";
                            placeholder.style.border = "1px dashed #ccc";
                            placeholder.style.margin = "10px 0";
                            placeholder.style.color = "#666";
                            excalidrawEl.replaceWith(placeholder);
                        }
                    }
                }

                // Ensure headings are bold and collect for TOC (H1-H3 only)
                const headings = contentDiv.querySelectorAll("h1, h2, h3, h4, h5, h6, strong");
                headings.forEach(heading => {
                    (heading as HTMLElement).style.fontWeight = "bold";

                    // Collect H1, H2, H3 for TOC (skip if already has toc-id)
                    const tagName = heading.tagName.toLowerCase();
                    if ((tagName === "h1" || tagName === "h2" || tagName === "h3") && !(heading as HTMLElement).hasAttribute("data-toc-id")) {
                        const level = parseInt(tagName.charAt(1));

                        // Use the helper function to clean heading text and remove emoji
                        const text = cleanHeadingText(heading as HTMLElement);

                        // Debug logging for text extraction
                        const originalText = (heading as HTMLElement).textContent || "";
                        if (originalText !== text && text) {
                            console.log(`[PDF Export TOC] Cleaned heading text: "${originalText}" -> "${text}"`);
                        }

                        // Only add to TOC if we have meaningful text and it's not just special characters
                        if (text && text.length > 0 && /[a-zA-Z0-9]/.test(text)) {
                            (heading as HTMLElement).setAttribute("data-toc-id", `toc-${tocEntries.length}`);
                            tocEntries.push({
                                title: text,
                                level: level,
                                yPosition: 0, // Will be calculated after DOM append
                                pageNumber: undefined
                            });
                        }
                    }
                });

                sectionWrapper.appendChild(contentDiv);
                exportContainer.appendChild(sectionWrapper);
            }

            // Append to DOM for rendering
            document.body.appendChild(exportContainer);

            // Wait for layout calculation and SVG rendering (increased for Excalidraw diagrams)
            await new Promise(resolve => setTimeout(resolve, 200));

            // Option A: fit oversized table rows to a single page by compacting typography.
            // This keeps rows atomic during pagination without introducing intra-row splits.
            const safePageHeightMm = availableHeight - 25; // Keep aligned with pagination safety budget.
            const maxRowHeightPx = Math.max(80, Math.floor((safePageHeightMm - 10) * mmToPxRatio));
            const minCellFontSizePx = 6;
            const minCellPaddingPx = 2;

            const allTableRows = exportContainer.querySelectorAll("table tr");
            allTableRows.forEach(rowNode => {
                const rowElement = rowNode as HTMLElement;
                let rowHeight = rowElement.getBoundingClientRect().height;

                if (rowHeight <= maxRowHeightPx) {
                    return;
                }

                const cells = Array.from(rowElement.querySelectorAll("th, td")) as HTMLElement[];
                if (cells.length === 0) {
                    return;
                }

                for (let attempt = 0; attempt < 14 && rowHeight > maxRowHeightPx; attempt++) {
                    let adjustedAnyCell = false;

                    cells.forEach(cell => {
                        const computed = window.getComputedStyle(cell);
                        const currentFontSize = parseFloat(computed.fontSize) || 10;
                        const currentPaddingTop = parseFloat(computed.paddingTop) || 6;
                        const currentPaddingBottom = parseFloat(computed.paddingBottom) || 6;
                        const currentPaddingLeft = parseFloat(computed.paddingLeft) || 4;
                        const currentPaddingRight = parseFloat(computed.paddingRight) || 4;

                        const nextFontSize = Math.max(minCellFontSizePx, currentFontSize - 0.75);
                        const nextPaddingTop = Math.max(minCellPaddingPx, currentPaddingTop - 0.5);
                        const nextPaddingBottom = Math.max(minCellPaddingPx, currentPaddingBottom - 0.5);
                        const nextPaddingLeft = Math.max(minCellPaddingPx, currentPaddingLeft - 0.5);
                        const nextPaddingRight = Math.max(minCellPaddingPx, currentPaddingRight - 0.5);

                        if (
                            nextFontSize < currentFontSize ||
                            nextPaddingTop < currentPaddingTop ||
                            nextPaddingBottom < currentPaddingBottom ||
                            nextPaddingLeft < currentPaddingLeft ||
                            nextPaddingRight < currentPaddingRight
                        ) {
                            adjustedAnyCell = true;
                        }

                        cell.style.fontSize = `${nextFontSize}px`;
                        cell.style.lineHeight = "1.0";
                        cell.style.paddingTop = `${nextPaddingTop}px`;
                        cell.style.paddingBottom = `${nextPaddingBottom}px`;
                        cell.style.paddingLeft = `${nextPaddingLeft}px`;
                        cell.style.paddingRight = `${nextPaddingRight}px`;
                        cell.style.wordBreak = "break-word";
                        cell.style.overflowWrap = "anywhere";
                        cell.style.whiteSpace = "normal";
                    });

                    if (!adjustedAnyCell) {
                        break;
                    }

                    rowHeight = rowElement.getBoundingClientRect().height;
                }

                if (rowHeight > maxRowHeightPx) {
                    console.warn(
                        `[PDF Export] Row still exceeds page-fit budget after Option A compaction: ${rowHeight.toFixed(1)}px > ${maxRowHeightPx}px`
                    );
                }
            });

            // Get actual dimensions
            const actualWidth = Math.max(exportContainer.offsetWidth, exportContainer.scrollWidth);
            const actualHeight = Math.max(exportContainer.offsetHeight, exportContainer.scrollHeight);

            console.log(`[PDF Export] Container dimensions: ${actualWidth}px × ${actualHeight}px`);

            // Calculate TOC entry positions and detect boundaries for smart pagination
            const containerRect = exportContainer.getBoundingClientRect();
            tocEntries.forEach((entry, idx) => {
                const element = exportContainer.querySelector(`[data-toc-id="toc-${idx}"]`);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    entry.yPosition = rect.top - containerRect.top;
                }
            });
            console.log(`[PDF Export] Collected ${tocEntries.length} TOC entries`);

            // Detect section, heading, and table boundaries BEFORE capturing for smart pagination
            const sectionBoundaries: Array<{
                yPos: number;
                yEnd: number;
                type: string;
                height: number;
                sectionIndex?: number;
                tableIndex?: number;
                rowIndex?: number;
            }> = [];
            const allSectionWrappers = exportContainer.querySelectorAll(".section-wrapper");

            allSectionWrappers.forEach((wrapper, index) => {
                const htmlWrapper = wrapper as HTMLElement;
                const rect = htmlWrapper.getBoundingClientRect();

                // Calculate relative position from top of container in pixels
                const relativeTopPx = rect.top - containerRect.top;

                sectionBoundaries.push({
                    yPos: relativeTopPx,
                    yEnd: relativeTopPx + rect.height,
                    height: rect.height,
                    type: `section-${index}`
                });

                // Track heading boundaries within sections
                const headings = htmlWrapper.querySelectorAll("h1, h2, h3, h4, h5, h6");
                const headingAnchors: Array<{ index: number; yPos: number; yEnd: number }> = [];
                headings.forEach((heading, hIndex) => {
                    const headingElement = heading as HTMLElement;
                    const headingRect = headingElement.getBoundingClientRect();
                    const headingRelativeTopPx = headingRect.top - containerRect.top;

                    // Get computed margins to include in boundary calculation
                    const computedStyle = window.getComputedStyle(headingElement);
                    const marginTop = parseFloat(computedStyle.marginTop) || 0;
                    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

                    // Keep heading protection tight to avoid creating large blank areas.
                    const safetyMarginTop = Math.max(4, marginTop + 2);
                    const safetyMarginBottom = Math.max(4, marginBottom + 2);

                    sectionBoundaries.push({
                        yPos: Math.max(0, headingRelativeTopPx - safetyMarginTop),
                        yEnd: headingRelativeTopPx + headingRect.height + safetyMarginBottom,
                        height: headingRect.height + safetyMarginTop + safetyMarginBottom,
                        type: `heading-${index}-${hIndex}`
                    });

                    headingAnchors.push({
                        index: hIndex,
                        yPos: Math.max(0, headingRelativeTopPx - safetyMarginTop),
                        yEnd: headingRelativeTopPx + headingRect.height + safetyMarginBottom
                    });
                });

                // Track table boundaries - CRITICAL for keeping tables together
                const tables = htmlWrapper.querySelectorAll("table");
                tables.forEach((table, tIndex) => {
                    const tableElement = table as HTMLElement;
                    const tableRect = tableElement.getBoundingClientRect();
                    const tableRelativeTopPx = tableRect.top - containerRect.top;

                    const tableBoundary = {
                        yPos: tableRelativeTopPx,
                        yEnd: tableRelativeTopPx + tableRect.height,
                        height: tableRect.height,
                        type: `table-${index}-${tIndex}`,
                        sectionIndex: index,
                        tableIndex: tIndex
                    };

                    sectionBoundaries.push(tableBoundary);
                    console.log(`[PDF Export] Detected ${tableBoundary.type}: y=${tableRelativeTopPx.toFixed(1)}px, height=${tableRect.height.toFixed(1)}px`);

                    // Track table row boundaries so page breaks never cut through a cell.
                    const rows = tableElement.querySelectorAll("tr");
                    let firstRowBoundary: { yPos: number; yEnd: number } | null = null;
                    for (const [rowIndex, row] of Array.from(rows).entries()) {
                        const rowElement = row as HTMLElement;
                        const rowRect = rowElement.getBoundingClientRect();
                        const rowRelativeTopPx = rowRect.top - containerRect.top;

                        const rowBoundary = {
                            yPos: rowRelativeTopPx,
                            yEnd: rowRelativeTopPx + rowRect.height,
                            height: rowRect.height,
                            type: `table-row-${index}-${tIndex}-${rowIndex}`,
                            sectionIndex: index,
                            tableIndex: tIndex,
                            rowIndex
                        };

                        sectionBoundaries.push(rowBoundary);

                        if (rowIndex === 0) {
                            firstRowBoundary = { yPos: rowBoundary.yPos, yEnd: rowBoundary.yEnd };
                        }
                    }

                    // Keep heading with the start of the following table (header + first row).
                    // This avoids splitting a section title from the table it introduces.
                    const precedingHeading = [...headingAnchors]
                        .filter(anchor => anchor.yEnd <= tableRelativeTopPx)
                        .sort((a, b) => b.yPos - a.yPos)[0];

                    if (precedingHeading && firstRowBoundary) {
                        const headingTableBoundary = {
                            yPos: precedingHeading.yPos,
                            yEnd: firstRowBoundary.yEnd,
                            height: firstRowBoundary.yEnd - precedingHeading.yPos,
                            type: `heading-table-${index}-${precedingHeading.index}-${tIndex}`,
                            sectionIndex: index,
                            tableIndex: tIndex
                        };

                        sectionBoundaries.push(headingTableBoundary);
                    }
                });

                // Do not track paragraph/list boundaries for protected pagination.
                // They produced excessive white-space and unstable break behavior.
            });

            // Also detect tables that might be outside section wrappers
            const allTables = exportContainer.querySelectorAll("table");
            console.log(`[PDF Export] Total tables found in container: ${allTables.length}`);
            allTables.forEach((table, globalIndex) => {
                const tableElement = table as HTMLElement;
                const tableRect = tableElement.getBoundingClientRect();
                const tableRelativeTopPx = tableRect.top - containerRect.top;

                // Check if this table is already tracked
                const alreadyTracked = sectionBoundaries.some(b => b.type.startsWith("table-") && Math.abs(b.yPos - tableRelativeTopPx) < 1);

                if (!alreadyTracked) {
                    const tableBoundary = {
                        yPos: tableRelativeTopPx,
                        yEnd: tableRelativeTopPx + tableRect.height,
                        height: tableRect.height,
                        type: `table-global-${globalIndex}`
                    };
                    sectionBoundaries.push(tableBoundary);
                    console.log(
                        `[PDF Export] Detected ${tableBoundary.type} (outside wrapper): y=${tableRelativeTopPx.toFixed(1)}px, height=${tableRect.height.toFixed(1)}px`
                    );
                }
            });

            // Track Excalidraw diagram boundaries - CRITICAL for keeping diagrams together
            const allExcalidrawDiagrams = exportContainer.querySelectorAll(".excalidraw-container, [data-excalidraw]");
            console.log(`[PDF Export] Total Excalidraw diagrams found: ${allExcalidrawDiagrams.length}`);
            allExcalidrawDiagrams.forEach((diagram, diagramIndex) => {
                const diagramElement = diagram as HTMLElement;
                const diagramRect = diagramElement.getBoundingClientRect();
                const diagramRelativeTopPx = diagramRect.top - containerRect.top;

                // Add generous safety margins to ensure the entire diagram (including any surrounding padding/margin) is kept together
                const safetyMarginPx = 20;

                const diagramBoundary = {
                    yPos: Math.max(0, diagramRelativeTopPx - safetyMarginPx),
                    yEnd: diagramRelativeTopPx + diagramRect.height + safetyMarginPx,
                    height: diagramRect.height + safetyMarginPx * 2,
                    type: `excalidraw-diagram-${diagramIndex}`
                };

                sectionBoundaries.push(diagramBoundary);
                console.log(`[PDF Export] Detected ${diagramBoundary.type}: y=${diagramRelativeTopPx.toFixed(1)}px, height=${diagramRect.height.toFixed(1)}px`);
            });

            sectionBoundaries.sort((a, b) => a.yPos - b.yPos);

            // Log boundary counts by type
            const boundaryTypeCounts = sectionBoundaries.reduce(
                (acc, b) => {
                    const typePrefix = b.type.split("-")[0];
                    acc[typePrefix] = (acc[typePrefix] || 0) + 1;
                    return acc;
                },
                {} as Record<string, number>
            );

            console.log(`[PDF Export] Detected ${sectionBoundaries.length} total boundaries:`, boundaryTypeCounts);

            // Capture the entire container at once
            const canvas = await html2canvas(exportContainer, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
                width: actualWidth,
                height: actualHeight,
                windowWidth: actualWidth,
                windowHeight: actualHeight
            });

            // Clean up
            document.body.removeChild(exportContainer);

            console.log(`[PDF Export] Canvas captured: ${canvas.width}px × ${canvas.height}px`);

            // Calculate image dimensions in mm
            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Calculate how many pages we need
            const totalPages = Math.ceil(imgHeight / availableHeight);
            console.log(`[PDF Export] Creating ${totalPages} pages`);

            // Split canvas into pages with smart section-aware pagination
            const mmToPixel = canvas.width / imgWidth;

            // Convert section boundaries from original pixels to canvas pixels
            const canvasScale = canvas.width / actualWidth;
            const sectionBoundariesPx = sectionBoundaries.map(b => ({
                yPos: b.yPos * canvasScale,
                yEnd: b.yEnd * canvasScale,
                height: b.height * canvasScale,
                type: b.type,
                sectionIndex: b.sectionIndex,
                tableIndex: b.tableIndex,
                rowIndex: b.rowIndex
            }));
            const canvasContext = canvas.getContext("2d", { willReadFrequently: true });

            type TableRegion = {
                sectionIndex: number;
                tableIndex: number;
                yPos: number;
                yEnd: number;
                headerRowStart: number;
                headerRowEnd: number;
                headerRowHeight: number;
            };

            const tableRegions: TableRegion[] = sectionBoundariesPx
                .filter(
                    boundary =>
                        boundary.type.startsWith("table-") &&
                        !boundary.type.startsWith("table-row-") &&
                        !boundary.type.startsWith("table-global-") &&
                        boundary.sectionIndex !== undefined &&
                        boundary.tableIndex !== undefined
                )
                .map(boundary => {
                    const headerRow = sectionBoundariesPx.find(
                        row =>
                            row.type.startsWith("table-row-") &&
                            row.sectionIndex === boundary.sectionIndex &&
                            row.tableIndex === boundary.tableIndex &&
                            row.rowIndex === 0
                    );

                    const headerRowStart = headerRow ? Math.floor(headerRow.yPos) : Math.floor(boundary.yPos);
                    const headerRowEnd = headerRow ? Math.floor(headerRow.yEnd) : Math.floor(boundary.yPos);
                    const headerRowHeight = Math.max(0, headerRowEnd - headerRowStart);

                    return {
                        sectionIndex: boundary.sectionIndex as number,
                        tableIndex: boundary.tableIndex as number,
                        yPos: boundary.yPos,
                        yEnd: boundary.yEnd,
                        headerRowStart,
                        headerRowEnd,
                        headerRowHeight
                    };
                });

            const findTableRegionAtY = (yPx: number): TableRegion | undefined => {
                return tableRegions.find(region => yPx >= Math.floor(region.yPos) && yPx < Math.floor(region.yEnd));
            };

            const getTableRows = (region: TableRegion): Array<{ yPos: number; yEnd: number; rowIndex: number }> => {
                return sectionBoundariesPx
                    .filter(
                        boundary =>
                            boundary.type.startsWith("table-row-") &&
                            boundary.sectionIndex === region.sectionIndex &&
                            boundary.tableIndex === region.tableIndex &&
                            boundary.rowIndex !== undefined
                    )
                    .map(boundary => ({
                        yPos: Math.floor(boundary.yPos),
                        yEnd: Math.ceil(boundary.yEnd),
                        rowIndex: boundary.rowIndex as number
                    }))
                    .sort((a, b) => a.yPos - b.yPos);
            };

            console.log(`[PDF Export] Canvas scale: ${canvasScale.toFixed(2)}, mmToPixel: ${mmToPixel.toFixed(2)}`);
            console.log(`[PDF Export] Available page height: ${availableHeight.toFixed(1)}mm (${(availableHeight * mmToPixel).toFixed(0)}px on canvas)`);

            // Log all table boundaries in canvas pixels
            const tableBoundaries = sectionBoundariesPx.filter(b => b.type.includes("table"));
            console.log(
                `[PDF Export] Table boundaries in canvas pixels:`,
                tableBoundaries.map(t => `${t.type}: ${t.yPos.toFixed(0)}-${t.yEnd.toFixed(0)}px (${t.height.toFixed(0)}px tall)`)
            );

            // Store page break positions for two-pass approach
            interface PageInfo {
                startYMm: number;
                heightMm: number;
                pageNumber: number;
                startYPx: number;
                heightPx: number;
                repeatedHeaderHeightPx: number;
                repeatedHeaderSourceYPx: number;
            }
            const pages: PageInfo[] = [];

            let currentYPxCursor = 0;
            let pageNumber = 0;

            // First pass: calculate all page breaks
            // CONSISTENT APPROACH: Fixed bottom safety zone on every page
            // This ensures slices NEVER land in the danger zone where text could be cut
            // Creates consistent page lengths and prevents ALL cuts
            const bottomSafetyZoneMm = 12; // Keep a small safety band while maximizing per-page text density
            const safePageHeight = availableHeight - bottomSafetyZoneMm;
            const safePageHeightPx = Math.floor(safePageHeight * mmToPixel);
            const availablePageHeightPx = Math.floor(availableHeight * mmToPixel);
            const minSliceHeightPx = Math.max(1, Math.floor(8 * mmToPixel)); // ~8mm minimum to avoid near-empty pages

            const isProtectedBoundary = (type: string): boolean => {
                return (
                    type.startsWith("table-row-") ||
                    type.startsWith("heading-table-") ||
                    type.startsWith("heading-") ||
                    type.startsWith("excalidraw-diagram-")
                );
            };

            const protectedIntervals = sectionBoundariesPx
                .filter(boundary => isProtectedBoundary(boundary.type))
                .map(boundary => ({
                    start: Math.floor(boundary.yPos),
                    end: Math.ceil(boundary.yEnd),
                    type: boundary.type
                }))
                .sort((a, b) => a.start - b.start);

            const findProtectedIntervalAt = (yPx: number): { start: number; end: number; type: string } | undefined => {
                return protectedIntervals.find(interval => yPx > interval.start && yPx < interval.end);
            };

            const cutsProtectedBoundary = (breakPx: number): boolean => {
                return sectionBoundariesPx.some(boundary => isProtectedBoundary(boundary.type) && boundary.yPos < breakPx && boundary.yEnd > breakPx);
            };

            const forceLegalBreak = (candidateBreakPx: number, minBreakPx: number, maxBreakPx: number): number => {
                let clamped = Math.max(minBreakPx, Math.min(maxBreakPx, Math.floor(candidateBreakPx)));

                // Iterate a few times to ensure we land outside all protected intervals.
                for (let attempt = 0; attempt < 24; attempt++) {
                    const crossingInterval = findProtectedIntervalAt(clamped);
                    if (!crossingInterval) {
                        return clamped;
                    }

                    const canMoveBefore = crossingInterval.start >= minBreakPx;
                    const canMoveAfter = crossingInterval.end <= maxBreakPx;

                    // For table-related protected blocks, prefer moving the break AFTER the
                    // block so we pack more rows on the current page and avoid header-only
                    // stubs at page bottoms.
                    const preferAfter =
                        crossingInterval.type.startsWith("table-row-") ||
                        crossingInterval.type.startsWith("heading-table-") ||
                        crossingInterval.type.startsWith("heading-") ||
                        crossingInterval.type.startsWith("excalidraw-diagram-");

                    if (preferAfter && canMoveAfter) {
                        clamped = crossingInterval.end;
                        continue;
                    }

                    if (canMoveBefore) {
                        clamped = crossingInterval.start;
                        continue;
                    }

                    if (canMoveAfter) {
                        clamped = crossingInterval.end;
                        continue;
                    }

                    // No legal move around this protected interval in the band.
                    // Keep current clamped value and let caller handle page fallback.
                    return clamped;
                }

                return clamped;
            };

            const snapBreakToWhitespace = (targetBreakPx: number, minBreakPx: number, maxBreakPx: number): number => {
                const clampedTarget = Math.max(minBreakPx, Math.min(maxBreakPx, Math.floor(targetBreakPx)));

                if (!canvasContext || maxBreakPx <= minBreakPx) {
                    return clampedTarget;
                }

                const searchRadiusPx = 120;
                const bandStart = Math.max(minBreakPx, clampedTarget - searchRadiusPx);
                const bandEnd = Math.min(maxBreakPx, clampedTarget + searchRadiusPx);
                const bandHeight = bandEnd - bandStart + 1;

                if (bandHeight <= 3) {
                    return clampedTarget;
                }

                let bandData: Uint8ClampedArray;
                try {
                    bandData = canvasContext.getImageData(0, bandStart, canvas.width, bandHeight).data;
                } catch {
                    return clampedTarget;
                }

                const sampleStepX = Math.max(1, Math.floor(canvas.width / 600));
                const sampledColumns = Math.ceil(canvas.width / sampleStepX);
                const rowInkRatio = new Array<number>(bandHeight).fill(0);

                for (let localY = 0; localY < bandHeight; localY++) {
                    let darkSamples = 0;

                    for (let x = 0; x < canvas.width; x += sampleStepX) {
                        const pixelIndex = (localY * canvas.width + x) * 4;
                        const alpha = bandData[pixelIndex + 3];

                        if (alpha < 16) {
                            continue;
                        }

                        const red = bandData[pixelIndex];
                        const green = bandData[pixelIndex + 1];
                        const blue = bandData[pixelIndex + 2];
                        const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

                        if (luminance < 247) {
                            darkSamples += 1;
                        }
                    }

                    rowInkRatio[localY] = darkSamples / sampledColumns;
                }

                const isWhitespaceRow = (globalY: number): boolean => {
                    if (globalY < bandStart || globalY > bandEnd) {
                        return false;
                    }

                    const localY = globalY - bandStart;
                    const previous = localY > 0 ? rowInkRatio[localY - 1] : rowInkRatio[localY];
                    const current = rowInkRatio[localY];
                    const next = localY + 1 < bandHeight ? rowInkRatio[localY + 1] : rowInkRatio[localY];
                    const threshold = 0.004;

                    return previous < threshold && current < threshold && next < threshold;
                };

                const isWhitespaceBand = (globalY: number, radius: number = 2): boolean => {
                    for (let y = globalY - radius; y <= globalY + radius; y++) {
                        if (!isWhitespaceRow(y)) {
                            return false;
                        }
                    }
                    return true;
                };

                let bestCandidate = clampedTarget;
                let bestScore = Number.POSITIVE_INFINITY;

                for (let offset = 0; offset <= searchRadiusPx; offset++) {
                    const candidates = offset === 0 ? [clampedTarget] : [clampedTarget - offset, clampedTarget + offset];

                    for (const candidate of candidates) {
                        if (candidate < minBreakPx || candidate > maxBreakPx) {
                            continue;
                        }

                        if (cutsProtectedBoundary(candidate)) {
                            continue;
                        }

                        const localY = candidate - bandStart;
                        if (localY >= 0 && localY < bandHeight) {
                            const ink = rowInkRatio[localY];
                            const distancePenalty = offset / Math.max(1, searchRadiusPx);
                            const score = ink + distancePenalty * 0.1;
                            if (score < bestScore) {
                                bestScore = score;
                                bestCandidate = candidate;
                            }
                        }

                        if (isWhitespaceBand(candidate, 2)) {
                            return candidate;
                        }
                    }
                }

                // Extended search for a true blank band before giving up.
                const extendedSearchRadiusPx = 260;
                const extStart = Math.max(minBreakPx, clampedTarget - extendedSearchRadiusPx);
                const extEnd = Math.min(maxBreakPx, clampedTarget + extendedSearchRadiusPx);

                for (let candidate = clampedTarget; candidate >= extStart; candidate--) {
                    if (cutsProtectedBoundary(candidate)) {
                        continue;
                    }
                    if (isWhitespaceBand(candidate, 1)) {
                        return candidate;
                    }
                }

                for (let candidate = clampedTarget + 1; candidate <= extEnd; candidate++) {
                    if (cutsProtectedBoundary(candidate)) {
                        continue;
                    }
                    if (isWhitespaceBand(candidate, 1)) {
                        return candidate;
                    }
                }

                // Absolute fallback: bias upward slightly to avoid slicing the same text row
                // across consecutive pages.
                return Math.max(minBreakPx, Math.min(maxBreakPx, bestCandidate - 2));
            };

            const isSliceMostlyBlank = (startPx: number, endPx: number): boolean => {
                if (!canvasContext) {
                    return false;
                }

                const start = Math.max(0, Math.floor(startPx));
                const end = Math.max(start + 1, Math.min(canvas.height, Math.floor(endPx)));
                const height = end - start;
                if (height <= 0) {
                    return true;
                }

                try {
                    const data = canvasContext.getImageData(0, start, canvas.width, height).data;
                    const sampleStepX = Math.max(1, Math.floor(canvas.width / 500));
                    const sampleStepY = Math.max(1, Math.floor(height / 120));

                    let darkSamples = 0;
                    let totalSamples = 0;

                    for (let y = 0; y < height; y += sampleStepY) {
                        for (let x = 0; x < canvas.width; x += sampleStepX) {
                            const i = (y * canvas.width + x) * 4;
                            const a = data[i + 3];
                            if (a < 16) {
                                totalSamples += 1;
                                continue;
                            }

                            const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
                            if (lum < 247) {
                                darkSamples += 1;
                            }
                            totalSamples += 1;
                        }
                    }

                    if (totalSamples === 0) {
                        return true;
                    }

                    const inkRatio = darkSamples / totalSamples;
                    return inkRatio < 0.0009;
                } catch {
                    return false;
                }
            };

            const rowInkRatioCache = new Map<number, number>();
            const sampleStepX = Math.max(1, Math.floor(canvas.width / 600));

            const getRowInkRatio = (globalY: number): number => {
                const y = Math.max(0, Math.min(canvas.height - 1, Math.floor(globalY)));
                const cached = rowInkRatioCache.get(y);
                if (cached !== undefined) {
                    return cached;
                }

                if (!canvasContext) {
                    return 0;
                }

                try {
                    const rowData = canvasContext.getImageData(0, y, canvas.width, 1).data;
                    let darkSamples = 0;
                    let sampled = 0;

                    for (let x = 0; x < canvas.width; x += sampleStepX) {
                        const i = x * 4;
                        const alpha = rowData[i + 3];
                        if (alpha >= 16) {
                            const luminance = 0.2126 * rowData[i] + 0.7152 * rowData[i + 1] + 0.0722 * rowData[i + 2];
                            if (luminance < 247) {
                                darkSamples += 1;
                            }
                        }
                        sampled += 1;
                    }

                    const ratio = sampled > 0 ? darkSamples / sampled : 0;
                    rowInkRatioCache.set(y, ratio);
                    return ratio;
                } catch {
                    return 0;
                }
            };

            const isWhitespaceBandGlobal = (globalY: number, radius: number = 2, threshold: number = 0.004): boolean => {
                const y = Math.floor(globalY);
                for (let rowY = y - radius; rowY <= y + radius; rowY++) {
                    if (rowY < 0 || rowY >= canvas.height) {
                        return false;
                    }
                    if (getRowInkRatio(rowY) >= threshold) {
                        return false;
                    }
                }
                return true;
            };

            while (currentYPxCursor < canvas.height) {
                const remainingHeightPx = canvas.height - currentYPxCursor;
                const activeTableAtPageStart = findTableRegionAtY(currentYPxCursor);
                const shouldRepeatHeader =
                    !!activeTableAtPageStart &&
                    activeTableAtPageStart.headerRowHeight > 0 &&
                    currentYPxCursor >= activeTableAtPageStart.headerRowEnd;
                const repeatedHeaderHeightPx = shouldRepeatHeader ? activeTableAtPageStart.headerRowHeight : 0;
                const repeatedHeaderSourceYPx = shouldRepeatHeader ? activeTableAtPageStart.headerRowStart : 0;

                // Leave room for repeated table header rows on continuation pages.
                const pageContentBudgetPx = Math.max(minSliceHeightPx, safePageHeightPx - repeatedHeaderHeightPx);
                const maxTablePageBudgetPx = Math.max(minSliceHeightPx, availablePageHeightPx - repeatedHeaderHeightPx);
                // Use the full per-page budget by default and rely on whitespace snapping +
                // protected-boundary legalization to avoid visual line cuts.
                const effectivePageBudgetPx = maxTablePageBudgetPx;

                // Use safe height - creates consistent spacing and prevents all cuts
                let heightToCapturePx = Math.min(effectivePageBudgetPx, remainingHeightPx);

                // Smart pagination: avoid cutting sections/headings/tables
                const currentYPx = currentYPxCursor;
                let nextPageBreakPx = currentYPx + heightToCapturePx;

                console.log(`[PDF Export] Page ${pageNumber + 1}: currentY=${currentYPx}px, nextBreak=${nextPageBreakPx}px`);

                // Intentionally avoid section-level forced breaks here.
                // This keeps pages densely packed and lets break logic choose the
                // lowest legal boundary near the actual page bottom.

                // Hard rule for table regions:
                // - Break only on table row boundaries.
                // - Fit as many full rows as possible on current page.
                // - If next row can't fit, move it to next page.
                // - If a single row is taller than budget, let that row own the page.
                let usedStrictTableRowBreak = false;
                if (activeTableAtPageStart) {
                    const tableRows = getTableRows(activeTableAtPageStart).filter(row => row.rowIndex > 0);
                    const remainingRows = tableRows.filter(row => row.yEnd > currentYPx);

                    if (remainingRows.length > 0) {
                        const maxBreakWithinBudget = Math.min(currentYPx + maxTablePageBudgetPx, canvas.height);
                        const rowsThatFit = remainingRows.filter(row => row.yEnd <= maxBreakWithinBudget);

                        if (rowsThatFit.length > 0) {
                            nextPageBreakPx = rowsThatFit[rowsThatFit.length - 1].yEnd;
                        } else {
                            // Oversized next row: still snap break to row boundary, even if this exceeds nominal budget.
                            nextPageBreakPx = Math.min(canvas.height, remainingRows[0].yEnd);
                        }

                        heightToCapturePx = Math.max(minSliceHeightPx, nextPageBreakPx - currentYPx);
                        usedStrictTableRowBreak = true;

                        console.log(
                            `[PDF Export] Page ${pageNumber + 1}: strict table-row break at ${nextPageBreakPx}px ` +
                                `(cursor ${currentYPx}px)`
                        );
                    }
                }

                // Re-check repeatedly because moving one break can cut another block.
                // Iterate until no protected boundary is crossed (or attempts exhausted).
                let adjustmentAttempts = 0;
                const maxAdjustmentAttempts = 12;

                while (!usedStrictTableRowBreak && adjustmentAttempts < maxAdjustmentAttempts) {
                    const boundariesSpanningBreak = sectionBoundariesPx.filter(
                        boundary => isProtectedBoundary(boundary.type) && boundary.yPos < nextPageBreakPx && boundary.yEnd > nextPageBreakPx
                    );

                    if (boundariesSpanningBreak.length === 0) {
                        break;
                    }

                    const firstCutBoundary = boundariesSpanningBreak.sort((a, b) => a.yPos - b.yPos)[0];

                    const breakBeforePx = Math.max(currentYPx, Math.floor(firstCutBoundary.yPos));
                    const breakBudgetPx =
                        firstCutBoundary.type.startsWith("table-row-") || firstCutBoundary.type.startsWith("heading-table-")
                            ? maxTablePageBudgetPx
                            : effectivePageBudgetPx;
                    const breakAfterPx = Math.min(Math.floor(firstCutBoundary.yEnd), currentYPx + breakBudgetPx, canvas.height);

                    const canBreakBefore = breakBeforePx - currentYPx >= minSliceHeightPx;
                    const canBreakAfter = breakAfterPx - currentYPx >= minSliceHeightPx;

                    // Prefer breaking AFTER protected heading/table/diagram blocks when legal
                    // to maximize page fill and avoid large whitespace holes.
                    // Fall back to breaking before if needed.
                    let proposedBreakPx = nextPageBreakPx;
                    const preferAfterBoundary =
                        firstCutBoundary.type.startsWith("table-row-") ||
                        firstCutBoundary.type.startsWith("heading-table-") ||
                        firstCutBoundary.type.startsWith("heading-") ||
                        firstCutBoundary.type.startsWith("excalidraw-diagram-");

                    if (preferAfterBoundary && canBreakAfter) {
                        proposedBreakPx = breakAfterPx;
                    } else if (canBreakBefore) {
                        proposedBreakPx = breakBeforePx;

                        // Exception: for the first body row in a table (rowIndex === 1), prefer
                        // breaking after that row so we don't leave a header-only stub page.
                        if (firstCutBoundary.type.startsWith("table-row-") && firstCutBoundary.rowIndex === 1 && canBreakAfter) {
                            proposedBreakPx = breakAfterPx;
                        }

                        // If we're about to break at the first row of a table, snap to the
                        // table boundary itself so we never leave a dangling top border line.
                        if (firstCutBoundary.type.startsWith("table-row-") && firstCutBoundary.rowIndex === 0) {
                            const parentTable = sectionBoundariesPx.find(
                                b =>
                                    b.type.startsWith("table-") &&
                                    b.sectionIndex === firstCutBoundary.sectionIndex &&
                                    b.tableIndex === firstCutBoundary.tableIndex
                            );

                            if (parentTable) {
                                const tableStartPx = Math.floor(parentTable.yPos);
                                if (tableStartPx - currentYPx >= minSliceHeightPx) {
                                    proposedBreakPx = tableStartPx;
                                }
                            }
                        }
                    } else if (canBreakAfter) {
                        proposedBreakPx = breakAfterPx;
                    }

                    // No meaningful adjustment possible (e.g. oversized protected block).
                    if (proposedBreakPx === nextPageBreakPx) {
                        break;
                    }

                    nextPageBreakPx = proposedBreakPx;
                    const adjustedHeightPx = Math.max(minSliceHeightPx, Math.floor(nextPageBreakPx - currentYPx));
                    heightToCapturePx = Math.min(remainingHeightPx, adjustedHeightPx);

                    console.log(
                        `[PDF Export] Page ${pageNumber + 1}: Adjusted to avoid cutting ${firstCutBoundary.type}, ` +
                            `range ${firstCutBoundary.yPos.toFixed(0)}-${firstCutBoundary.yEnd.toFixed(0)}px`
                    );

                    adjustmentAttempts += 1;
                }

                // Safety guard against non-progress due to rounding or boundary alignment.
                if (heightToCapturePx <= 0) {
                    const fallbackHeightPx = Math.min(effectivePageBudgetPx, Math.max(minSliceHeightPx, remainingHeightPx));
                    heightToCapturePx = fallbackHeightPx;
                    nextPageBreakPx = currentYPx + fallbackHeightPx;
                }

                // Final pass: avoid cutting through text raster rows.
                // Snap to a nearby whitespace row so breaks land above/below lines,
                // not on top of rendered glyph pixels.
                const minBreakPx = currentYPx + minSliceHeightPx;
                const maxBreakPx = Math.min(currentYPx + maxTablePageBudgetPx, canvas.height);
                if (!usedStrictTableRowBreak) {
                    nextPageBreakPx = snapBreakToWhitespace(nextPageBreakPx, minBreakPx, maxBreakPx);
                    nextPageBreakPx = forceLegalBreak(nextPageBreakPx, minBreakPx, maxBreakPx);
                }

                // Final hard stop: never cut protected content (especially table rows/cells).
                if (cutsProtectedBoundary(nextPageBreakPx)) {
                    const crossing = findProtectedIntervalAt(nextPageBreakPx);
                    if (crossing) {
                        const isTableRowCrossing = crossing.type.startsWith("table-row-");
                        const maxRowBoundaryPx = Math.min(currentYPx + maxTablePageBudgetPx, canvas.height);

                        if (isTableRowCrossing && crossing.end <= maxRowBoundaryPx) {
                            nextPageBreakPx = crossing.end;
                        } else if (isTableRowCrossing) {
                            // Border snap rule: when crossing a row, snap to row end even if beyond nominal budget.
                            nextPageBreakPx = Math.min(canvas.height, crossing.end);
                        } else if (crossing.start >= minBreakPx) {
                            nextPageBreakPx = crossing.start;
                        } else if (crossing.end <= maxBreakPx) {
                            nextPageBreakPx = crossing.end;
                        }
                    }
                }

                // Packing rule at table starts:
                // If this slice contains only a table header row (rowIndex 0) but not the
                // first body row (rowIndex 1), extend the break through that first body row.
                // This avoids large blank areas where the next page starts with repeated header
                // + first row while the previous page ended with a header-only stub.
                const tableRegionStartingOnSlice = tableRegions
                    .filter(region => Math.floor(region.yPos) >= currentYPx && Math.floor(region.yPos) < nextPageBreakPx)
                    .sort((a, b) => a.yPos - b.yPos)[0];

                if (tableRegionStartingOnSlice) {
                    const rows = getTableRows(tableRegionStartingOnSlice);
                    const headerRow = rows.find(row => row.rowIndex === 0);
                    const firstBodyRow = rows.find(row => row.rowIndex === 1);

                    // Use remaining page budget aggressively for table starts: fit as many
                    // complete rows as possible up to the full available page height (not the
                    // conservative safe height used for non-table text blocks).
                    const maxPackBreakPx = Math.min(currentYPx + maxTablePageBudgetPx, canvas.height);
                    const rowsThatCanFit = rows.filter(row => row.yEnd <= maxPackBreakPx);
                    if (rowsThatCanFit.length > 0) {
                        const packBreakPx = rowsThatCanFit[rowsThatCanFit.length - 1].yEnd;
                        if (packBreakPx > nextPageBreakPx) {
                            nextPageBreakPx = packBreakPx;
                            console.log(
                                `[PDF Export] Page ${pageNumber + 1}: packed table-start slice to row ${rowsThatCanFit[rowsThatCanFit.length - 1].rowIndex}`
                            );
                        }
                    }

                    const headerRowInSlice =
                        !!headerRow &&
                        headerRow.yPos >= currentYPx &&
                        headerRow.yEnd <= nextPageBreakPx;
                    const firstBodyRowInSlice = !!firstBodyRow && firstBodyRow.yEnd <= nextPageBreakPx;

                    if (headerRowInSlice && firstBodyRow && !firstBodyRowInSlice) {
                        const packedBreakPx = Math.min(canvas.height, firstBodyRow.yEnd);
                        if (packedBreakPx > nextPageBreakPx) {
                            nextPageBreakPx = packedBreakPx;
                            console.log(
                                `[PDF Export] Page ${pageNumber + 1}: extended break to include first table body row ` +
                                    `(header-only stub prevention)`
                            );
                        }
                    }
                }

                heightToCapturePx = Math.min(remainingHeightPx, Math.max(1, nextPageBreakPx - currentYPx));

                // Clamp to remaining content so we never overflow canvas bounds.
                heightToCapturePx = Math.min(heightToCapturePx, remainingHeightPx);
                heightToCapturePx = Math.max(1, Math.floor(heightToCapturePx));
                const heightToCaptureMm = heightToCapturePx / mmToPixel;

                // Store page info for second pass
                pages.push({
                    startYMm: currentYPx / mmToPixel,
                    heightMm: heightToCaptureMm,
                    pageNumber: pageNumber,
                    startYPx: currentYPx,
                    heightPx: Math.max(1, heightToCapturePx),
                    repeatedHeaderHeightPx,
                    repeatedHeaderSourceYPx
                });

                currentYPxCursor += heightToCapturePx;
                pageNumber++;
            }

            // Second pass boundary normalization:
            // validate each inter-page cut and move it to a legal whitespace band if needed.
            // This enforces "fit if enough room, otherwise move to next page" at cut points.
            const maxBoundarySearchPx = 260;
            for (let i = 0; i < pages.length - 1; i++) {
                const previousPage = pages[i];
                const nextPage = pages[i + 1];
                const nextNextStart = i + 2 < pages.length ? pages[i + 2].startYPx : canvas.height;
                const boundaryY = Math.floor(nextPage.startYPx);

                const boundaryLooksUnsafe = cutsProtectedBoundary(boundaryY) || !isWhitespaceBandGlobal(boundaryY, 2);
                if (!boundaryLooksUnsafe) {
                    continue;
                }

                const minBoundaryY = Math.floor(previousPage.startYPx + minSliceHeightPx);
                const maxBoundaryY = Math.floor(nextNextStart - minSliceHeightPx);
                if (minBoundaryY >= maxBoundaryY) {
                    continue;
                }

                let relocatedBoundary: number | null = null;
                for (let offset = 1; offset <= maxBoundarySearchPx; offset++) {
                    const upward = boundaryY - offset;
                    if (
                        upward >= minBoundaryY &&
                        upward <= maxBoundaryY &&
                        !cutsProtectedBoundary(upward) &&
                        isWhitespaceBandGlobal(upward, 2)
                    ) {
                        relocatedBoundary = upward;
                        break;
                    }

                    const downward = boundaryY + offset;
                    if (
                        downward >= minBoundaryY &&
                        downward <= maxBoundaryY &&
                        !cutsProtectedBoundary(downward) &&
                        isWhitespaceBandGlobal(downward, 2)
                    ) {
                        relocatedBoundary = downward;
                        break;
                    }
                }

                if (relocatedBoundary === null) {
                    continue;
                }

                nextPage.startYPx = relocatedBoundary;
                console.log(
                    `[PDF Export] Boundary adjust: moved page cut ${i + 1}->${i + 2} from ${boundaryY}px to ${relocatedBoundary}px`
                );
            }

            // Recompute contiguous page geometry and repeated table header info after boundary normalization.
            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const nextStart = i + 1 < pages.length ? pages[i + 1].startYPx : canvas.height;

                page.startYPx = Math.max(0, Math.floor(page.startYPx));
                page.heightPx = Math.max(1, Math.floor(Math.min(nextStart - page.startYPx, canvas.height - page.startYPx)));
                page.startYMm = page.startYPx / mmToPixel;
                page.heightMm = page.heightPx / mmToPixel;

                const tableAtStart = findTableRegionAtY(page.startYPx);
                const shouldRepeatHeader =
                    !!tableAtStart &&
                    tableAtStart.headerRowHeight > 0 &&
                    page.startYPx >= tableAtStart.headerRowEnd;

                page.repeatedHeaderHeightPx = shouldRepeatHeader ? tableAtStart.headerRowHeight : 0;
                page.repeatedHeaderSourceYPx = shouldRepeatHeader ? tableAtStart.headerRowStart : 0;
            }

            // Remove trailing near-blank slices caused by raster rounding or tail whitespace.
            while (pages.length > 1) {
                const last = pages[pages.length - 1];
                const start = last.startYPx;
                const end = Math.min(canvas.height, last.startYPx + last.heightPx);
                if (!isSliceMostlyBlank(start, end)) {
                    break;
                }

                console.log(
                    `[PDF Export] Dropping trailing blank slice ${pages.length}: ${start}-${end}px`
                );
                pages.pop();
            }

            // Calculate which page each TOC entry is on
            tocEntries.forEach(entry => {
                const entryYPx = entry.yPosition * canvasScale;

                for (const page of pages) {
                    if (entryYPx >= page.startYPx && entryYPx < page.startYPx + page.heightPx) {
                        entry.pageNumber = page.pageNumber + 2; // +2 because TOC is page 1
                        break;
                    }
                }
            });

            console.log(
                `[PDF Export] TOC entries with page numbers:`,
                tocEntries.map(e => `${e.title} -> p${e.pageNumber}`)
            );

            // Now create a new PDF with TOC and headers/footers
            const finalPdf = new jsPDF("p", "mm", "letter");
            let hasToc = tocEntries.length > 0;

            // Add TOC page if we have entries
            if (hasToc) {
                finalPdf.setFontSize(20);
                finalPdf.setFont("helvetica", "bold");
                finalPdf.text("Table of Contents", sideMargin, topMargin + headerHeight + 10);

                let tocY = topMargin + headerHeight + 25;
                finalPdf.setFontSize(11);

                tocEntries.forEach(entry => {
                    if (tocY > pageHeight - bottomMargin - 20) {
                        finalPdf.addPage();
                        tocY = topMargin + headerHeight + 10;
                    }

                    const indent = (entry.level - 1) * 5;
                    const maxWidth = contentWidth - indent - 15;

                    if (entry.level === 1) {
                        finalPdf.setFont("helvetica", "bold");
                    } else {
                        finalPdf.setFont("helvetica", "normal");
                    }

                    // Title on left
                    finalPdf.text(entry.title, sideMargin + indent, tocY, { maxWidth });

                    // Page number on right
                    const pageNumText = String(entry.pageNumber || "?");
                    const pageNumWidth = finalPdf.getTextWidth(pageNumText);
                    finalPdf.text(pageNumText, pageWidth - sideMargin - pageNumWidth, tocY);

                    tocY += entry.level === 1 ? 8 : 6;
                });

                // Add footer to TOC page
                finalPdf.setFontSize(9);
                finalPdf.setFont("helvetica", "normal");
                finalPdf.setTextColor(100, 100, 100);
                if (workProductTitle) {
                    finalPdf.text(workProductTitle, sideMargin, pageHeight - bottomMargin + footerHeight);
                }
                finalPdf.text("Page 1", pageWidth - sideMargin - finalPdf.getTextWidth("Page 1"), pageHeight - bottomMargin + footerHeight);
                finalPdf.setTextColor(0, 0, 0);

                // Add page for content
                finalPdf.addPage();
            }

            // Second pass: render all content pages with headers and footers.
            // Render slices from contiguous pixel boundaries to prevent cumulative
            // mm/px rounding drift that can clip text between pages.

            const totalContentPages = pages.length;
            for (let i = 0; i < totalContentPages; i++) {
                if (i > 0) {
                    finalPdf.addPage();
                }

                const pageInfo = pages[i];
                const nextPageStartYPx = i + 1 < pages.length ? pages[i + 1].startYPx : canvas.height;

                // Ensure each page ends exactly where the next page starts.
                const actualStartY = Math.max(0, Math.floor(pageInfo.startYPx));
                const actualHeight = Math.max(1, Math.floor(Math.min(nextPageStartYPx - actualStartY, canvas.height - actualStartY)));

                console.log(
                    `[PDF Export] Page ${i + 1}: pixel range ${actualStartY}-${actualStartY + actualHeight} ` +
                        `(${(actualStartY / mmToPixel).toFixed(1)}-${((actualStartY + actualHeight) / mmToPixel).toFixed(1)}mm)`
                );

                // Create page canvas
                const pageCanvas = document.createElement("canvas");
                pageCanvas.width = canvas.width;
                const repeatedHeaderHeightPx = pageInfo.repeatedHeaderHeightPx || 0;
                const repeatedHeaderSourceYPx = pageInfo.repeatedHeaderSourceYPx || 0;
                pageCanvas.height = actualHeight + repeatedHeaderHeightPx;

                const ctx = pageCanvas.getContext("2d");
                if (ctx) {
                    if (repeatedHeaderHeightPx > 0) {
                        // Repeat table header row on continuation pages.
                        ctx.drawImage(
                            canvas,
                            0,
                            repeatedHeaderSourceYPx,
                            canvas.width,
                            repeatedHeaderHeightPx,
                            0,
                            0,
                            canvas.width,
                            repeatedHeaderHeightPx
                        );
                    }

                    // Draw page body below optional repeated header.
                    ctx.drawImage(
                        canvas,
                        0,
                        actualStartY,
                        canvas.width,
                        actualHeight,
                        0,
                        repeatedHeaderHeightPx,
                        canvas.width,
                        actualHeight
                    );

                    const finalPageHeightMm = pageCanvas.height / mmToPixel;
                    const pageData = pageCanvas.toDataURL("image/jpeg", 0.95);

                    // Add header
                    finalPdf.setFontSize(9);
                    finalPdf.setFont("helvetica", "normal");
                    finalPdf.setTextColor(100, 100, 100);
                    if (workProductTitle) {
                        finalPdf.text(workProductTitle, sideMargin, topMargin - 3);
                    }
                    finalPdf.setTextColor(0, 0, 0);

                    // Add content image at normal position (no offset needed since we cropped)
                    finalPdf.addImage(pageData, "JPEG", sideMargin, topMargin + headerHeight, imgWidth, finalPageHeightMm);

                    // Add footer
                    finalPdf.setFontSize(9);
                    finalPdf.setFont("helvetica", "normal");
                    finalPdf.setTextColor(100, 100, 100);
                    const currentPageNum = hasToc ? i + 2 : i + 1;
                    const totalPages = hasToc ? totalContentPages + 1 : totalContentPages;
                    const pageText = `Page ${currentPageNum} of ${totalPages}`;
                    const pageTextWidth = finalPdf.getTextWidth(pageText);
                    finalPdf.text(pageText, pageWidth - sideMargin - pageTextWidth, pageHeight - bottomMargin + footerHeight);

                    if (workProductTitle) {
                        finalPdf.text(workProductTitle, sideMargin, pageHeight - bottomMargin + footerHeight);
                    }
                    finalPdf.setTextColor(0, 0, 0);
                }
            }

            // Save the PDF with TOC and headers/footers
            finalPdf.save(filename);
            console.log(`[PDF Export] Successfully exported ${totalContentPages} pages${hasToc ? " with TOC" : ""} to ${filename}`);
            return true;
        } else {
            // Fallback: Use current editor content only
            console.log("[PDF Export] No sections data provided, using current editor content");
            const editorContent = document.querySelector(".tiptap-editor-content") as HTMLElement;
            if (!editorContent) {
                throw new Error("Editor content not found");
            }

            const canvas = await html2canvas(editorContent, {
                scale: 1.5,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
                windowWidth: editorContent.scrollWidth,
                windowHeight: editorContent.scrollHeight
            });

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const pdf = new jsPDF("p", "mm", "a4");
            let heightLeft = imgHeight;
            let position = 0;
            const imgData = canvas.toDataURL("image/jpeg", 0.85);

            pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(filename);
            return true;
        }
    } catch (error) {
        console.error("Error exporting to PDF:", error);
        throw error;
    }
}

/**
 * Convert HTML element to PNG buffer by rendering it in a temporary container
 */
async function elementToPng(htmlString: string): Promise<{ buffer: Uint8Array; width: number; height: number }> {
    try {
        // Create a temporary container in the actual DOM
        const tempContainer = document.createElement("div");
        tempContainer.style.position = "absolute";
        tempContainer.style.left = "-9999px";
        tempContainer.style.top = "0";
        tempContainer.style.visibility = "visible";
        tempContainer.style.backgroundColor = "#ffffff";
        tempContainer.innerHTML = htmlString;

        // Append to body so it gets rendered
        document.body.appendChild(tempContainer);

        // Wait for rendering (especially important for SVGs)
        await new Promise(resolve => setTimeout(resolve, 200));

        // Get dimensions
        const rect = tempContainer.getBoundingClientRect();
        const width = Math.max(rect.width, 300);
        const height = Math.max(rect.height, 200);

        console.log(`[elementToPng] Rendering element: ${width}x${height}`);

        // Use html2canvas to capture the element
        const canvas = await html2canvas(tempContainer, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: width,
            height: height,
            windowWidth: width,
            windowHeight: height
        });

        // Clean up
        document.body.removeChild(tempContainer);

        console.log(`[elementToPng] Canvas created: ${canvas.width}x${canvas.height}`);

        // Convert canvas to blob then to array buffer
        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                blob => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("Failed to create blob from canvas"));
                    }
                },
                "image/png",
                1.0
            );
        });

        const arrayBuffer = await blob.arrayBuffer();
        const buffer = new Uint8Array(arrayBuffer);

        console.log(`[elementToPng] PNG buffer created: ${buffer.length} bytes`);

        return {
            buffer: buffer,
            width: canvas.width,
            height: canvas.height
        };
    } catch (error) {
        console.error("Failed to convert element to PNG:", error);
        throw error;
    }
}

/**
 * Export TipTap editor content to Word (.docx)
 * UPDATED: Now supports exporting all sections when sectionsData is provided
 */
export async function exportToWord(editor: Editor, filename: string = "document.docx", sectionsData?: SectionData[], workProductTitle?: string) {
    try {
        let html: string;

        if (sectionsData && sectionsData.length > 0) {
            console.log(`[Word Export] Exporting ${sectionsData.length} sections`);
            // Note: We don't add titleHtml here because the title page
            // is handled separately in convertHTMLToDocx as a proper title page
            // (not as an H1 heading that would interfere with TOC)

            // Combine all sections into a single HTML document with Mermaid processing
            const mermaidDiagrams: Map<string, { svg: string; code: string }> = new Map();

            const sectionsHtml = await Promise.all(
                sectionsData.map(async (section, index) => {
                    // First, parse Excalidraw blocks (convert <!-- EXCALIDRAW_START --> markers to HTML)
                    let markdownContent = parseExcalidrawBlocks(section.content || "");

                    // Log how many Excalidraw blocks were found in this section
                    const excalidrawCount = (markdownContent.match(/data-excalidraw/g) || []).length;
                    if (excalidrawCount > 0) {
                        console.log(`[Word Export] Section ${index} has ${excalidrawCount} Excalidraw block(s) after parsing`);
                    }

                    // Then, process Mermaid diagrams BEFORE converting markdown to HTML

                    // Look for code blocks with mermaid language in the raw markdown
                    const mermaidRegex = /```mermaid\s+([\s\S]*?)```/g;
                    let match;
                    const mermaidReplacements: Array<{ original: string; replacement: string }> = [];

                    while ((match = mermaidRegex.exec(markdownContent)) !== null) {
                        const fullMatch = match[0];
                        const mermaidCode = match[1].trim();

                        try {
                            console.log(`[Word Export] Processing Mermaid diagram in section ${index}`);
                            mermaid.initialize({ startOnLoad: false, theme: "default" });
                            const mermaidId = `mermaid-word-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                            const { svg } = await mermaid.render(mermaidId, mermaidCode);

                            // Store the diagram data with a unique placeholder
                            const placeholderId = `MERMAID_PLACEHOLDER_${mermaidId}`;
                            mermaidDiagrams.set(placeholderId, { svg, code: mermaidCode });

                            // Use a unique marker that won't be escaped by marked.parse
                            const replacement = `\n\n<!-- ${placeholderId} -->\n\n`;
                            mermaidReplacements.push({ original: fullMatch, replacement });
                            console.log(`[Word Export] Successfully rendered Mermaid diagram with placeholder ${placeholderId}`);
                        } catch (error) {
                            console.error("[Word Export] Failed to render Mermaid diagram:", error);
                            mermaidReplacements.push({
                                original: fullMatch,
                                replacement: "\n\n<!-- MERMAID_ERROR -->[Mermaid Diagram - Rendering Error]\n\n"
                            });
                        }
                    }

                    // Apply all Mermaid replacements to the markdown
                    for (const { original, replacement } of mermaidReplacements) {
                        markdownContent = markdownContent.replace(original, replacement);
                    }

                    // Now convert the modified markdown to HTML
                    let contentHtml = await marked.parse(markdownContent);

                    // Replace placeholders with actual div elements after markdown processing
                    for (const [placeholderId, { svg, code }] of mermaidDiagrams.entries()) {
                        const divHtml = `<div data-mermaid="true" data-code="${code.replace(/"/g, "&quot;")}" data-svg="${encodeURIComponent(svg)}"></div>`;
                        contentHtml = contentHtml.replace(`<!-- ${placeholderId} -->`, divHtml);
                    }

                    return `${contentHtml}`;
                })
            );

            html = sectionsHtml.join("\n");
            console.log(`[Word Export] Generated HTML with ${(html.match(/data-mermaid/g) || []).length} Mermaid diagram(s)`);
        } else {
            // Fallback: Use current editor content only
            console.log("[Word Export] No sections data provided, using current editor content");
            html = editor.getHTML();
        }

        const doc = await convertHTMLToDocx(html, workProductTitle);

        // Generate and save the document
        console.log(`[Word Export] Generating .docx file`);
        const blob = await Packer.toBlob(doc);
        console.log(`[Word Export] File size: ${(blob.size / 1024).toFixed(2)} KB`);
        FileSaver.saveAs(blob, filename);

        console.log(`[Word Export] Successfully exported to ${filename} - Please open in Microsoft Word (not WordPad)`);
        return true;
    } catch (error) {
        console.error("Error exporting to Word:", error);
        throw error;
    }
}

/**
 * Export TipTap editor content to PDF by first generating a Word document
 * and then converting it to PDF via the backend.
 *
 * This approach provides better quality PDFs by leveraging the Word export
 * which handles formatting, tables, diagrams better than direct PDF generation.
 *
 * @param editor - TipTap editor instance
 * @param filename - Desired PDF filename
 * @param sectionsData - Optional array of sections to export
 * @param workProductTitle - Optional work product title
 * @param token - Authentication token for API call
 */
export async function exportToPDFViaWord(
    editor: Editor,
    filename: string = "document.pdf",
    sectionsData?: SectionData[],
    workProductTitle?: string,
    token?: string
): Promise<boolean> {
    try {
        console.log("[PDF Export via WeasyPrint] Starting conversion process");
        const minExpectedPdfBytes = 2048;

        const decodeHtmlEntities = (value: string): string => {
            const textarea = document.createElement("textarea");
            textarea.innerHTML = value;
            return textarea.value;
        };

        const decodeBase64Unicode = (value: string): string | null => {
            try {
                return decodeURIComponent(escape(atob(value)));
            } catch {
                return null;
            }
        };

        const extractMermaidCodeFromElement = (element: HTMLElement): string => {
            const encoding = (element.getAttribute("data-code-encoding") || "").trim().toLowerCase();
            const dataCode = element.getAttribute("data-code") || "";

            if (dataCode && encoding === "base64") {
                const decoded = decodeBase64Unicode(dataCode);
                if (decoded && decoded.trim()) {
                    return decoded.trim();
                }
            }

            if (dataCode.trim()) {
                return decodeHtmlEntities(dataCode).trim();
            }

            const nestedMermaidCode = element.querySelector("code.language-mermaid")?.textContent || "";
            if (nestedMermaidCode.trim()) {
                return nestedMermaidCode.trim();
            }

            return "";
        };

        const diagramImageInlineStyle = "display:block;max-width:100%;height:auto;max-height:7.2in;object-fit:contain;";

        const svgToPngDataUrl = async (svg: string): Promise<string | null> => {
            try {
                const viewBoxMatch = svg.match(/viewBox=["'][^"']*\s([\d.]+)\s([\d.]+)["']/i);
                const widthMatch = svg.match(/width=["']([\d.]+)(?:px)?["']/i);
                const heightMatch = svg.match(/height=["']([\d.]+)(?:px)?["']/i);

                const width = viewBoxMatch?.[1] ? Number(viewBoxMatch[1]) : Number(widthMatch?.[1] || 1200);
                const height = viewBoxMatch?.[2] ? Number(viewBoxMatch[2]) : Number(heightMatch?.[1] || 700);
                const maxTargetWidth = 1400;
                const maxTargetHeight = 760;
                const scale = Math.min(maxTargetWidth / Math.max(width, 1), maxTargetHeight / Math.max(height, 1), 1);
                const targetWidth = Math.max(1, Math.floor(width * scale));
                const targetHeight = Math.max(1, Math.floor(height * scale));

                const img = document.createElement("img");
                img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
                await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error("Failed to load Mermaid SVG as image"));
                });

                const canvas = document.createElement("canvas");
                canvas.width = targetWidth;
                canvas.height = targetHeight;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    return null;
                }

                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                return canvas.toDataURL("image/png");
            } catch (error) {
                console.warn("[PDF Export via WeasyPrint] Mermaid SVG->PNG conversion failed", error);
                return null;
            }
        };

        const mermaidRenderConfig = {
            startOnLoad: false,
            theme: "default",
            securityLevel: "strict" as const,
            flowchart: {
                htmlLabels: false,
                useMaxWidth: true,
            },
        };

        const renderMermaidMarkupForWeasy = async (mermaidCode: string, mermaidId: string): Promise<string> => {
            mermaid.initialize(mermaidRenderConfig);
            const { svg } = await mermaid.render(mermaidId, mermaidCode);

            if (svg.includes("<foreignObject")) {
                console.warn(`[PDF Export via WeasyPrint] Mermaid ${mermaidId} contains foreignObject; converting to PNG fallback`);
                const pngDataUrl = await svgToPngDataUrl(svg);
                if (pngDataUrl) {
                    return `<img src="${pngDataUrl}" alt="Mermaid diagram" style="${diagramImageInlineStyle}" />`;
                }
            }

            return svg;
        };

        const materializeMermaidBlocksForWeasy = async (sourceHtml: string): Promise<string> => {
            const container = document.createElement("div");
            container.innerHTML = sourceHtml;

            const mermaidDivs = Array.from(container.querySelectorAll("[data-mermaid]"));
            for (let index = 0; index < mermaidDivs.length; index += 1) {
                const element = mermaidDivs[index] as HTMLElement;
                const rawSvg = element.getAttribute("data-svg");
                const mermaidCode = extractMermaidCodeFromElement(element);

                let markup = "";
                if (rawSvg) {
                    try {
                        const decodedSvg = decodeURIComponent(rawSvg);
                        if (decodedSvg.includes("<foreignObject")) {
                            const pngDataUrl = await svgToPngDataUrl(decodedSvg);
                            if (pngDataUrl) {
                                markup = `<img src="${pngDataUrl}" alt="Mermaid diagram" style="${diagramImageInlineStyle}" />`;
                            }
                        } else {
                            markup = decodedSvg;
                        }
                    } catch (error) {
                        console.warn("[PDF Export via WeasyPrint] Failed decoding Mermaid SVG payload", error);
                    }
                }

                if (!markup && mermaidCode) {
                    try {
                        const mermaidId = `mermaid-weasy-inline-${Date.now()}-${index}`;
                        markup = await renderMermaidMarkupForWeasy(mermaidCode, mermaidId);
                    } catch (error) {
                        console.warn("[PDF Export via WeasyPrint] Failed rendering Mermaid data-code payload", error);
                    }
                }

                const replacement = document.createElement("div");
                replacement.className = "mermaid-diagram";
                replacement.innerHTML = markup || "[Mermaid diagram unavailable in PDF export]";
                element.replaceWith(replacement);
            }

            return container.innerHTML;
        };

        const decodeExcalidrawDataAttribute = (value: string): string => {
            const textarea = document.createElement("textarea");
            textarea.innerHTML = value;
            return textarea.value;
        };

        const renderExcalidrawMarkupForWeasy = async (dataAttr: string): Promise<string | null> => {
            try {
                const decodedData = decodeExcalidrawDataAttribute(dataAttr);
                const excalidrawData = JSON.parse(decodedData);

                const svg = await exportToSvg({
                    elements: excalidrawData.elements || [],
                    appState: {
                        ...(excalidrawData.appState || {}),
                        exportWithDarkMode: false,
                    },
                    files: excalidrawData.files || {},
                });

                if (svg?.outerHTML) {
                    const pngDataUrl = await svgToPngDataUrl(svg.outerHTML);
                    if (pngDataUrl) {
                        return `<img src="${pngDataUrl}" alt="Wireframe" style="${diagramImageInlineStyle}" />`;
                    }

                    return svg.outerHTML;
                }

                return null;
            } catch (error) {
                console.warn("[PDF Export via WeasyPrint] Failed rendering Excalidraw block", error);
                return null;
            }
        };

        const materializeExcalidrawBlocksForWeasy = async (sourceHtml: string): Promise<string> => {
            const container = document.createElement("div");
            container.innerHTML = sourceHtml;

            const excalidrawDivs = Array.from(container.querySelectorAll("[data-excalidraw]"));
            for (const element of excalidrawDivs) {
                const htmlElement = element as HTMLElement;
                const dataAttr = htmlElement.getAttribute("data-excalidraw-data") || "";

                const renderedMarkup = dataAttr ? await renderExcalidrawMarkupForWeasy(dataAttr) : null;

                const replacement = document.createElement("div");
                replacement.className = "excalidraw-diagram";
                replacement.innerHTML = renderedMarkup || "[Wireframe unavailable in PDF export]";
                htmlElement.replaceWith(replacement);
            }

            return container.innerHTML;
        };

        const sanitizeWireframeHtml = (rawHtml: string): string => {
            const parser = new DOMParser();
            const parsed = parser.parseFromString(`<div>${rawHtml}</div>`, "text/html");
            const root = parsed.body.firstElementChild as HTMLElement | null;
            if (!root) {
                return "";
            }

            const allowedTags = new Set([
                "div", "section", "article", "header", "footer", "main", "aside", "nav",
                "h1", "h2", "h3", "h4", "h5", "h6", "p", "span", "strong", "em", "small",
                "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td",
                "form", "label", "input", "select", "option", "button", "textarea", "br", "hr",
            ]);

            const allowedAttrs = new Set([
                "class", "id", "style", "type", "value", "placeholder", "name", "for",
                "disabled", "checked", "selected", "readonly", "aria-label", "role",
            ]);

            const allElements = Array.from(root.querySelectorAll("*"));
            for (const element of allElements) {
                const tag = element.tagName.toLowerCase();
                if (!allowedTags.has(tag)) {
                    element.replaceWith(parsed.createTextNode(element.textContent || ""));
                    continue;
                }

                for (const attr of Array.from(element.attributes)) {
                    const attrName = attr.name.toLowerCase();
                    const attrValue = attr.value || "";

                    const isEventHandler = attrName.startsWith("on");
                    const isDisallowedAttr = !allowedAttrs.has(attrName);
                    const isJavascriptUrl = /javascript:/i.test(attrValue);

                    if (isEventHandler || isDisallowedAttr || isJavascriptUrl) {
                        element.removeAttribute(attr.name);
                        continue;
                    }

                    if (attrName === "style") {
                        const safeStyle = attrValue
                            .split(";")
                            .map(rule => rule.trim())
                            .filter(rule => {
                                const key = rule.split(":")[0]?.trim().toLowerCase();
                                return [
                                    "display", "width", "height", "min-width", "max-width",
                                    "min-height", "max-height", "margin", "margin-left", "margin-right",
                                    "margin-top", "margin-bottom", "padding", "padding-left",
                                    "padding-right", "padding-top", "padding-bottom", "border",
                                    "border-left", "border-right", "border-top", "border-bottom",
                                    "border-radius", "background", "background-color", "color",
                                    "font-size", "font-weight", "line-height", "text-align",
                                    "vertical-align", "gap", "grid-template-columns", "grid-template-rows",
                                ].includes(key || "");
                            })
                            .join("; ");

                        if (safeStyle) {
                            element.setAttribute("style", safeStyle);
                        } else {
                            element.removeAttribute("style");
                        }
                    }
                }
            }

            return root.innerHTML;
        };

        const materializeWireframeHtmlBlocksForWeasy = (sourceHtml: string): string => {
            const container = document.createElement("div");
            container.innerHTML = sourceHtml;

            const codeBlocks = Array.from(container.querySelectorAll("pre > code"));
            let wireframeFound = 0;
            let wireframeRendered = 0;

            for (const codeElement of codeBlocks) {
                const rawCode = codeElement.textContent || "";
                if (!rawCode.trim()) {
                    continue;
                }

                const maybeHtml = decodeHtmlEntities(rawCode).trim();
                const hasUiTags = /<(div|form|input|select|button|textarea|label|table|section|article)\b/i.test(maybeHtml);
                if (!hasUiTags) {
                    continue;
                }

                wireframeFound += 1;
                const safeHtml = sanitizeWireframeHtml(maybeHtml);
                if (!safeHtml.trim()) {
                    continue;
                }

                const wrapper = document.createElement("div");
                wrapper.className = "wireframe-html-diagram";
                wrapper.style.border = "1px solid #d1d5db";
                wrapper.style.padding = "10px";
                wrapper.style.margin = "8px 0";
                wrapper.innerHTML = safeHtml;

                const preElement = codeElement.closest("pre");
                if (preElement?.parentNode) {
                    preElement.parentNode.replaceChild(wrapper, preElement);
                }

                wireframeRendered += 1;
            }

            return container.innerHTML;
        };

        const materializeSvgPassthroughBlocksForWeasy = async (sourceHtml: string): Promise<string> => {
            const container = document.createElement("div");
            container.innerHTML = sourceHtml;

            const passthroughNodes = Array.from(container.querySelectorAll("[data-svg-passthrough]"));
            for (const node of passthroughNodes) {
                const element = node as HTMLElement;
                const encodedSvg = element.getAttribute("data-svg-html") || "";

                let renderedMarkup = "";
                if (encodedSvg) {
                    try {
                        const decodedSvg = decodeBase64Unicode(encodedSvg) || atob(encodedSvg);
                        const pngDataUrl = await svgToPngDataUrl(decodedSvg);
                        if (pngDataUrl) {
                            renderedMarkup = `<img src="${pngDataUrl}" alt="Wireframe" style="${diagramImageInlineStyle}" />`;
                        } else {
                            renderedMarkup = decodedSvg;
                        }
                    } catch (error) {
                        console.warn("[PDF Export via WeasyPrint] Failed to decode data-svg-passthrough block", error);
                    }
                }

                const replacement = document.createElement("div");
                replacement.className = "wireframe-svg-passthrough";
                replacement.innerHTML = renderedMarkup || "[Wireframe SVG passthrough unavailable in PDF export]";
                element.replaceWith(replacement);
            }

            return container.innerHTML;
        };

        const enforceHeadingKeepWithNext = (sourceHtml: string): string => {
            const container = document.createElement("div");
            container.innerHTML = sourceHtml;

            const headings = Array.from(container.querySelectorAll("h1, h2, h3, h4, h5, h6"));
            for (const heading of headings) {
                const next = heading.nextElementSibling as HTMLElement | null;
                if (!next) {
                    continue;
                }

                const isVisualOrTabularBlock =
                    next.tagName.toLowerCase() === "table" ||
                    next.tagName.toLowerCase() === "svg" ||
                    next.tagName.toLowerCase() === "img" ||
                    next.querySelector("table, svg, img") !== null;

                if (!isVisualOrTabularBlock) {
                    continue;
                }

                const wrapper = document.createElement("div");
                wrapper.className = "keep-with-next";
                wrapper.style.breakInside = "avoid";
                // @ts-expect-error paged media property is valid for print layout
                wrapper.style.pageBreakInside = "avoid";

                const parent = heading.parentNode;
                if (!parent) {
                    continue;
                }

                parent.insertBefore(wrapper, heading);
                wrapper.appendChild(heading);
                wrapper.appendChild(next);
            }

            return container.innerHTML;
        };

        // Step 1: Build HTML payload with existing Mermaid->SVG preprocessing.
        let html: string;

        const normalizedSections = (sectionsData || []).map(section => ({
            section_title: section.section_title || "",
            content: section.content || "",
        }));
        const sectionsWithContent = normalizedSections.filter(section => section.content.trim().length > 0);

        if (sectionsWithContent.length > 0) {
            console.log(`[PDF Export via WeasyPrint] Processing ${sectionsWithContent.length} non-empty sections`);

            const mermaidDiagrams: Map<string, { svg: string; code: string }> = new Map();

            const sectionsHtml = await Promise.all(
                sectionsWithContent.map(async (section, index) => {
                    let markdownContent = parseExcalidrawBlocks(section.content || "");

                    const mermaidRegex = /```mermaid\s+([\s\S]*?)```/g;
                    let match;
                    const mermaidReplacements: Array<{ original: string; replacement: string }> = [];

                    while ((match = mermaidRegex.exec(markdownContent)) !== null) {
                        const fullMatch = match[0];
                        const mermaidCode = match[1].trim();

                        try {
                            const mermaidId = `mermaid-weasy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                            const renderedMarkup = await renderMermaidMarkupForWeasy(mermaidCode, mermaidId);

                            const placeholderId = `MERMAID_PLACEHOLDER_${mermaidId}`;
                            mermaidDiagrams.set(placeholderId, { svg: renderedMarkup, code: mermaidCode });

                            const replacement = `\n\n<!-- ${placeholderId} -->\n\n`;
                            mermaidReplacements.push({ original: fullMatch, replacement });
                        } catch (error) {
                            console.error(`[PDF Export via WeasyPrint] Error rendering Mermaid diagram in section ${index}:`, error);
                        }
                    }

                    for (const { original, replacement } of mermaidReplacements) {
                        markdownContent = markdownContent.replace(original, replacement);
                    }

                    marked.setOptions({
                        breaks: true,
                        gfm: true
                    });

                    let htmlContent = marked.parse(markdownContent) as string;

                    mermaidDiagrams.forEach((diagramData, placeholderId) => {
                        const placeholder = `<!-- ${placeholderId} -->`;
                        if (htmlContent.includes(placeholder)) {
                            htmlContent = htmlContent.replace(placeholder, diagramData.svg);
                        }
                    });

                    return `<section data-section-index="${index}"><h2>${section.section_title || ""}</h2>${htmlContent}</section>`;
                })
            );

            html = sectionsHtml.join("\n\n");

            const sectionHtmlProbe = document.createElement("div");
            sectionHtmlProbe.innerHTML = html;
            const sectionProbeText = (sectionHtmlProbe.textContent || "").replace(/\s+/g, " ").trim();
            const sectionProbeHasRichContent =
                sectionHtmlProbe.querySelector("table, img, svg, [data-svg-passthrough], [data-excalidraw], [data-mermaid]") !== null;
            const sectionProbeHasParagraphs = sectionHtmlProbe.querySelectorAll("p").length > 1;

            if (sectionProbeText.length < 120 && !sectionProbeHasRichContent && !sectionProbeHasParagraphs) {
                console.warn("[PDF Export via WeasyPrint] Sections snapshot appears stale/near-empty; falling back to editor HTML");
                html = editor.getHTML();
            }
        } else {
            console.log("[PDF Export via WeasyPrint] Sections are empty/stale, using current editor content");
            html = editor.getHTML();
        }

        html = await materializeMermaidBlocksForWeasy(html);
        html = await materializeExcalidrawBlocksForWeasy(html);
        html = materializeWireframeHtmlBlocksForWeasy(html);
        html = await materializeSvgPassthroughBlocksForWeasy(html);
        html = enforceHeadingKeepWithNext(html);

        // Step 2: Send HTML payload to backend WeasyPrint converter.
        console.log("[PDF Export via WeasyPrint] Sending HTML payload to backend");

        // Determine API base URL
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const apiUrl = `${apiBaseUrl}/api/export/weasyprint-pdf`;

        // Prepare headers
        const headers: HeadersInit = {
            "Content-Type": "application/json"
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const payload: Record<string, unknown> = {
            filename,
            workProductTitle,
            html,
        };

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || errorData.message || `Conversion failed: ${response.statusText}`);
        }

        // Step 3: Download the PDF
        console.log("[PDF Export via WeasyPrint] Receiving PDF from backend");
        const pdfBlob = await response.blob();

        console.log(`[PDF Export via WeasyPrint] PDF file size: ${(pdfBlob.size / 1024).toFixed(2)} KB`);

        // Guard against server-side blank/truncated conversion results.
        // Throw so the toolbar flow can fall back to client-side PDF export.
        const responseContentType = response.headers.get('content-type') || '';
        if (!responseContentType.toLowerCase().includes('application/pdf')) {
            throw new Error(`[PDF Export via WeasyPrint] Unexpected response content type: ${responseContentType || 'unknown'}`);
        }
        if (pdfBlob.size < minExpectedPdfBytes) {
            throw new Error(`[PDF Export via WeasyPrint] Backend returned an unexpectedly small PDF (${pdfBlob.size} bytes)`);
        }

        FileSaver.saveAs(pdfBlob, filename);

        console.log(`[PDF Export via WeasyPrint] Successfully exported to ${filename}`);
        return true;
    } catch (error) {
        console.error("[PDF Export via WeasyPrint] Error:", error);
        throw error;
    }
}

/**
 * Detect regional paper size preference based on user's timezone and locale
 * Returns Letter size for North America, A4 for rest of world
 * PRIMARY: Uses timezone for accurate location detection
 * FALLBACK: Uses browser locale
 * DEFAULT: Letter size (US standard) for ambiguous cases
 */
function getRegionalPaperSize(): { width: number; height: number; name: string } {
    // LETTER size definition (North America)
    const LETTER_SIZE = {
        width: 12240, // 8.5 inches in twips (1 inch = 1440 twips)
        height: 15840, // 11 inches in twips
        name: 'Letter (8.5" × 11")'
    };

    // A4 size definition (Rest of world)
    const A4_SIZE = {
        width: 11906, // 210mm (8.27 inches) in twips
        height: 16838, // 297mm (11.69 inches) in twips
        name: "A4 (210mm × 297mm)"
    };

    // STEP 1: Check timezone (most reliable for location)
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log(`[Word Export] Timezone detected: ${timezone}`);

        // Check if timezone matches North America (Letter size)
        if (timezone.startsWith("America/") || timezone.startsWith("US/") || timezone.startsWith("Canada/")) {
            console.log(`[Word Export] North American timezone detected, using Letter size`);
            return LETTER_SIZE;
        }

        // European timezones (A4)
        if (timezone.startsWith("Europe/")) {
            console.log(`[Word Export] European timezone detected, using A4 size`);
            return A4_SIZE;
        }

        // Australian/NZ timezones (A4)
        if (timezone.startsWith("Australia/") || timezone.startsWith("Pacific/Auckland")) {
            console.log(`[Word Export] Australia/NZ timezone detected, using A4 size`);
            return A4_SIZE;
        }

        // Asian timezones (A4)
        if (timezone.startsWith("Asia/")) {
            console.log(`[Word Export] Asian timezone detected, using A4 size`);
            return A4_SIZE;
        }
    } catch (error) {
        console.warn(`[Word Export] Timezone detection failed:`, error);
    }

    // STEP 2: Fallback to browser locale
    const locale = navigator.language || navigator.languages?.[0] || "en-US";
    console.log(`[Word Export] Browser locale detected: ${locale}`);

    // Extract country code from locale (e.g., "en-US" -> "US")
    const localeparts = locale.split("-");
    const country = localeparts.length > 1 ? localeparts[1].toUpperCase() : "";

    console.log(`[Word Export] Extracted country code: "${country}"`);

    // North American countries (Letter size)
    const letterSizeCountries = ["US", "CA", "MX", "PR"];

    // A4-using countries
    const a4Countries = ["GB", "AU", "NZ", "IE", "IN", "ZA", "SG", "MY", "PK", "BD", "LK"];

    // European countries (all use A4)
    const europeanCountries = [
        "AT",
        "BE",
        "BG",
        "HR",
        "CY",
        "CZ",
        "DK",
        "EE",
        "FI",
        "FR",
        "DE",
        "GR",
        "HU",
        "IS",
        "IE",
        "IT",
        "LV",
        "LT",
        "LU",
        "MT",
        "NL",
        "NO",
        "PL",
        "PT",
        "RO",
        "SK",
        "SI",
        "ES",
        "SE",
        "CH"
    ];

    // Check for North American countries first (Letter size)
    if (letterSizeCountries.includes(country)) {
        console.log(`[Word Export] Country "${country}" uses Letter size`);
        return LETTER_SIZE;
    }

    // Check for known A4 countries
    if (a4Countries.includes(country) || europeanCountries.includes(country)) {
        console.log(`[Word Export] Country "${country}" uses A4 size`);
        return A4_SIZE;
    }

    // STEP 3: DEFAULT to Letter (US standard)
    // This handles cases where both timezone and locale detection fail or are ambiguous
    console.log(`[Word Export] No specific region detected, defaulting to Letter size (US standard)`);
    return LETTER_SIZE;
}

/**
 * Convert HTML from TipTap to DOCX structure
 */
async function convertHTMLToDocx(html: string, workProductTitle?: string): Promise<Document> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const children: (Paragraph | Table)[] = [];

    // Track headings as we create them (since we can't read them back from Paragraph objects)
    const headingTracker: Array<{ level: number; text: string }> = [];

    /**
     * Helper function to process a Mermaid diagram element and convert it to a Word image
     */
    const processMermaidDiagram = async (element: Element): Promise<Paragraph[]> => {
        const results: Paragraph[] = [];

        try {
            console.log("[Word Export] Converting Mermaid diagram to image");
            // Check if we have pre-rendered SVG data
            const svgData = element.getAttribute("data-svg");
            let svg: string;

            if (svgData) {
                // Use pre-rendered SVG
                svg = decodeURIComponent(svgData);
                console.log("[Word Export] Using pre-rendered SVG, length:", svg.length);
            } else {
                // Fall back to rendering from code
                const code = element.getAttribute("data-code");
                if (!code) {
                    throw new Error("No Mermaid code or SVG data found");
                }
                console.log("[Word Export] Rendering from code");

                mermaid.initialize({ startOnLoad: false, theme: "default" });
                const mermaidId = `mermaid-word-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const result = await mermaid.render(mermaidId, code);
                svg = result.svg;
            }

            // Convert SVG to PNG
            console.log("[Word Export] Converting SVG to PNG");
            const { buffer, width, height } = await elementToPng(svg);
            console.log(`[Word Export] PNG conversion successful: ${width}x${height}, buffer size: ${buffer.length}`);

            // Calculate scaled dimensions (max 600x400)
            const maxWidth = 600;
            const maxHeight = 400;
            let finalWidth = width;
            let finalHeight = height;

            if (width > maxWidth || height > maxHeight) {
                const aspectRatio = width / height;
                if (width > height) {
                    finalWidth = maxWidth;
                    finalHeight = maxWidth / aspectRatio;
                } else {
                    finalHeight = maxHeight;
                    finalWidth = maxHeight * aspectRatio;
                }
            }

            console.log(`[Word Export] Final dimensions: ${Math.round(finalWidth)}x${Math.round(finalHeight)}`);

            results.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            type: "png",
                            data: buffer,
                            transformation: {
                                width: Math.round(finalWidth),
                                height: Math.round(finalHeight)
                            }
                        } as any)
                    ],
                    spacing: { before: 200, after: 200 }
                })
            );
            console.log("[Word Export] Mermaid diagram added to document");
        } catch (error) {
            console.error("Failed to render Mermaid diagram for Word:", error);
            results.push(
                new Paragraph({
                    children: [new TextRun({ text: `[Mermaid Diagram - Rendering Error: ${error instanceof Error ? error.message : "Unknown error"}]` })],
                    spacing: { before: 200, after: 200 }
                })
            );
        }

        return results;
    };

    // Helper to recursively search for Mermaid diagrams in any element
    const findMermaidDiagrams = (element: Element): Element[] => {
        const diagrams: Element[] = [];
        if (element.hasAttribute("data-mermaid")) {
            diagrams.push(element);
        }
        // Recursively search children
        for (const child of Array.from(element.children)) {
            diagrams.push(...findMermaidDiagrams(child));
        }
        return diagrams;
    };

    /**
     * Helper function to process an Excalidraw diagram element and convert it to a Word image
     */
    const processExcalidrawDiagram = async (element: Element): Promise<Paragraph[]> => {
        const results: Paragraph[] = [];

        try {
            console.log("[Word Export] Converting Excalidraw diagram to image");
            const dataAttr = element.getAttribute("data-excalidraw-data");

            if (!dataAttr) {
                throw new Error("No Excalidraw data found");
            }

            // Decode HTML entities from the attribute
            const decodedData = dataAttr
                .replace(/&amp;/g, "&")
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">");

            // Parse Excalidraw JSON data
            const excalidrawData = JSON.parse(decodedData);
            console.log(`[Word Export] Rendering Excalidraw diagram with ${excalidrawData.elements?.length || 0} elements`);

            // Render Excalidraw to SVG
            const svg = await exportToSvg({
                elements: excalidrawData.elements || [],
                appState: {
                    ...excalidrawData.appState,
                    exportWithDarkMode: false
                },
                files: excalidrawData.files || {}
            });

            // Convert SVG to PNG
            console.log("[Word Export] Converting Excalidraw SVG to PNG");
            const { buffer, width, height } = await elementToPng(svg.outerHTML);
            console.log(`[Word Export] PNG conversion successful: ${width}x${height}, buffer size: ${buffer.length}`);

            // Calculate scaled dimensions (max 600x400)
            const maxWidth = 600;
            const maxHeight = 400;
            let finalWidth = width;
            let finalHeight = height;

            if (width > maxWidth || height > maxHeight) {
                const aspectRatio = width / height;
                if (width > height) {
                    finalWidth = maxWidth;
                    finalHeight = maxWidth / aspectRatio;
                } else {
                    finalHeight = maxHeight;
                    finalWidth = maxHeight * aspectRatio;
                }
            }

            console.log(`[Word Export] Creating ImageRun with dimensions: ${finalWidth}x${finalHeight}`);
            results.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            type: "png",
                            data: buffer,
                            transformation: {
                                width: finalWidth,
                                height: finalHeight
                            }
                        })
                    ],
                    spacing: { before: 200, after: 200 }
                })
            );
            console.log("[Word Export] Successfully added Excalidraw diagram to document");
        } catch (error) {
            console.error("Failed to render Excalidraw diagram for Word:", error);
            results.push(
                new Paragraph({
                    children: [new TextRun({ text: `[Excalidraw Diagram - Rendering Error: ${error instanceof Error ? error.message : "Unknown error"}]` })],
                    spacing: { before: 200, after: 200 }
                })
            );
        }

        return results;
    };

    // Helper to recursively search for Excalidraw diagrams in any element
    const findExcalidrawDiagrams = (element: Element): Element[] => {
        const diagrams: Element[] = [];
        if (element.hasAttribute("data-excalidraw")) {
            diagrams.push(element);
        }
        // Recursively search children
        for (const child of Array.from(element.children)) {
            diagrams.push(...findExcalidrawDiagrams(child));
        }
        return diagrams;
    };

    // Process each top-level element
    const processElement = async (element: Element): Promise<(Paragraph | Table)[]> => {
        const results: (Paragraph | Table)[] = [];
        const tagName = element.tagName.toLowerCase();

        // First check if this element or any descendant contains a Mermaid diagram
        const mermaidDiagrams = findMermaidDiagrams(element);
        if (mermaidDiagrams.length > 0 && tagName === "p") {
            // If a paragraph contains Mermaid diagrams, process them directly
            for (const diagram of mermaidDiagrams) {
                const diagramResults = await processMermaidDiagram(diagram);
                results.push(...diagramResults);
            }
            return results;
        }

        // Check if this element or any descendant contains an Excalidraw diagram
        const excalidrawDiagrams = findExcalidrawDiagrams(element);
        if (excalidrawDiagrams.length > 0 && tagName === "p") {
            // If a paragraph contains Excalidraw diagrams, process them directly
            for (const diagram of excalidrawDiagrams) {
                const diagramResults = await processExcalidrawDiagram(diagram);
                results.push(...diagramResults);
            }
            return results;
        }

        switch (tagName) {
            case "p":
                results.push(
                    new Paragraph({
                        children: processInlineElements(element),
                        alignment: getAlignment(element),
                        spacing: { after: 200 }
                    })
                );
                break;

            case "h1":
            case "h2":
            case "h3":
            case "h4":
            case "h5":
            case "h6":
                // Track headings for TOC (only H1, H2, H3)
                const headingLevel = parseInt(tagName.charAt(1)); // Extract number from "h1", "h2", etc.
                if (headingLevel >= 1 && headingLevel <= 3) {
                    const headingText = element.textContent || "";
                    if (headingText.trim()) {
                        headingTracker.push({ level: headingLevel, text: headingText.trim() });
                        console.log(`[Word Export] Tracked heading: "${headingText.trim()}" (H${headingLevel})`);
                    }
                }

                // Use custom heading processing to preserve emoji colors
                // Don't use built-in heading styles as they force color on all text including emojis
                results.push(
                    new Paragraph({
                        children: processHeadingElements(element, tagName),
                        alignment: getAlignment(element),
                        spacing: { before: 240, after: 120 }
                    })
                );
                break;

            case "ul":
            case "ol":
                const directChildren = Array.from(element.children).filter(child => child.tagName.toLowerCase() === "li");
                directChildren.forEach((li, index) => {
                    // Check if this li has nested lists
                    const nestedList = li.querySelector("ul, ol");

                    if (nestedList) {
                        // Process the text content before the nested list
                        const textContent = Array.from(li.childNodes)
                            .filter(node => node.nodeType === Node.TEXT_NODE || (node.nodeType === Node.ELEMENT_NODE && !(node as Element).matches("ul, ol")))
                            .map(node => node.textContent)
                            .join("")
                            .trim();

                        if (textContent) {
                            results.push(
                                new Paragraph({
                                    children: [new TextRun({ text: textContent })],
                                    bullet: tagName === "ul" ? { level: 0 } : undefined,
                                    numbering: tagName === "ol" ? { reference: "default-numbering", level: 0 } : undefined,
                                    spacing: { after: 100 }
                                })
                            );
                        }

                        const nestedItems = Array.from(nestedList.children).filter(child => child.tagName.toLowerCase() === "li");
                        nestedItems.forEach(nestedLi => {
                            results.push(
                                new Paragraph({
                                    children: processInlineElements(nestedLi),
                                    bullet: nestedList.tagName.toLowerCase() === "ul" ? { level: 1 } : undefined,
                                    numbering: nestedList.tagName.toLowerCase() === "ol" ? { reference: "default-numbering", level: 1 } : undefined,
                                    spacing: { after: 100 }
                                })
                            );
                        });
                    } else {
                        // No nested list, process normally
                        results.push(
                            new Paragraph({
                                children: processInlineElements(li),
                                bullet: tagName === "ul" ? { level: 0 } : undefined,
                                numbering: tagName === "ol" ? { reference: "default-numbering", level: 0 } : undefined,
                                spacing: { after: 100 }
                            })
                        );
                    }
                });
                break;

            case "blockquote":
                const quoteRuns = processInlineElements(element);
                // Apply italics to all text runs
                quoteRuns.forEach(run => {
                    (run as any).italics = true;
                });
                results.push(
                    new Paragraph({
                        children: quoteRuns,
                        indent: { left: 720 },
                        spacing: { before: 200, after: 200 }
                    })
                );
                break;

            case "pre":
                const code = element.querySelector("code") || element;
                results.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: code.textContent || "",
                                font: "Courier New",
                                size: 20
                            })
                        ],
                        shading: { fill: "F4F4F4" },
                        spacing: { before: 200, after: 200 }
                    })
                );
                break;

            case "table":
                const tableRows: TableRow[] = [];
                const rows = element.querySelectorAll("tr");

                // Determine number of columns from first row
                let numColumns = 0;
                if (rows.length > 0) {
                    const firstRowCells = rows[0].querySelectorAll("td, th");
                    numColumns = firstRowCells.length;
                }

                // Calculate equal column width percentage
                const columnWidth = numColumns > 0 ? Math.floor(100 / numColumns) : 100;

                rows.forEach((row, rowIndex) => {
                    const cells: TableCell[] = [];
                    const cellElements = row.querySelectorAll("td, th");

                    cellElements.forEach(cell => {
                        const isHeader = cell.tagName.toLowerCase() === "th";
                        cells.push(
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        children: [
                                            new TextRun({
                                                text: cell.textContent || "",
                                                bold: isHeader
                                            })
                                        ]
                                    })
                                ],
                                width: { size: columnWidth, type: WidthType.PERCENTAGE },
                                shading: isHeader ? { fill: "D9D9D9" } : undefined,
                                margins: {
                                    top: 100,
                                    bottom: 100,
                                    left: 100,
                                    right: 100
                                }
                            })
                        );
                    });

                    tableRows.push(
                        new TableRow({
                            children: cells,
                            cantSplit: false,
                            tableHeader: rowIndex === 0
                        })
                    );
                });

                if (tableRows.length > 0) {
                    results.push(
                        new Table({
                            rows: tableRows,
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            columnWidths: Array(numColumns).fill(columnWidth * 100), // Convert to DXA units
                            borders: {
                                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                                left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                                right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
                            }
                        })
                    );
                }
                break;

            case "hr":
                results.push(
                    new Paragraph({
                        children: [new TextRun({ text: "" })],
                        border: {
                            bottom: {
                                color: "000000",
                                space: 1,
                                style: BorderStyle.SINGLE,
                                size: 6
                            }
                        },
                        spacing: { before: 120, after: 120 }
                    })
                );
                break;

            case "div":
                // Check for Mermaid diagrams
                if (element.hasAttribute("data-mermaid")) {
                    const diagramResults = await processMermaidDiagram(element);
                    results.push(...diagramResults);
                    break;
                }
                // Check for Excalidraw diagrams
                else if (element.hasAttribute("data-excalidraw")) {
                    const diagramResults = await processExcalidrawDiagram(element);
                    results.push(...diagramResults);
                    break;
                }
                // Check for SVG passthrough divs (from TipTap)
                else if (element.hasAttribute("data-svg-passthrough")) {
                    try {
                        // Get the element HTML and render it to PNG
                        const elementHTML = element.outerHTML;
                        const { buffer, width, height } = await elementToPng(elementHTML);

                        // Calculate scaled dimensions (max 600x400)
                        const maxWidth = 600;
                        const maxHeight = 400;
                        let finalWidth = width;
                        let finalHeight = height;

                        if (width > maxWidth || height > maxHeight) {
                            const aspectRatio = width / height;
                            if (width > height) {
                                finalWidth = maxWidth;
                                finalHeight = maxWidth / aspectRatio;
                            } else {
                                finalHeight = maxHeight;
                                finalWidth = maxHeight * aspectRatio;
                            }
                        }

                        results.push(
                            new Paragraph({
                                children: [
                                    new ImageRun({
                                        type: "png",
                                        data: buffer,
                                        transformation: {
                                            width: Math.round(finalWidth),
                                            height: Math.round(finalHeight)
                                        }
                                    } as any)
                                ],
                                spacing: { before: 200, after: 200 }
                            })
                        );
                    } catch (error) {
                        console.error("Failed to process SVG passthrough:", error);
                        results.push(
                            new Paragraph({
                                children: [new TextRun({ text: "[Diagram]" })],
                                spacing: { before: 200, after: 200 }
                            })
                        );
                    }
                    break;
                }
                // For other divs, process children
                else if (element.children.length > 0) {
                    for (const child of Array.from(element.children)) {
                        const childResults = await processElement(child);
                        results.push(...childResults);
                    }
                } else if (element.textContent?.trim()) {
                    results.push(
                        new Paragraph({
                            children: [new TextRun({ text: element.textContent })]
                        })
                    );
                }
                break;

            case "svg":
                try {
                    const svgHTML = element.outerHTML;
                    const { buffer, width, height } = await elementToPng(svgHTML);

                    // Calculate scaled dimensions (max 600x400)
                    const maxWidth = 600;
                    const maxHeight = 400;
                    let finalWidth = width;
                    let finalHeight = height;

                    if (width > maxWidth || height > maxHeight) {
                        const aspectRatio = width / height;
                        if (width > height) {
                            finalWidth = maxWidth;
                            finalHeight = maxWidth / aspectRatio;
                        } else {
                            finalHeight = maxHeight;
                            finalWidth = maxHeight * aspectRatio;
                        }
                    }

                    results.push(
                        new Paragraph({
                            children: [
                                new ImageRun({
                                    type: "png",
                                    data: buffer,
                                    transformation: {
                                        width: Math.round(finalWidth),
                                        height: Math.round(finalHeight)
                                    }
                                } as any)
                            ],
                            spacing: { before: 200, after: 200 }
                        })
                    );
                } catch (error) {
                    console.error("Failed to convert SVG to image:", error);
                    results.push(
                        new Paragraph({
                            children: [new TextRun({ text: "[Diagram]" })],
                            spacing: { before: 200, after: 200 }
                        })
                    );
                }
                break;

            case "img":
                try {
                    const imgElement = element as HTMLImageElement;
                    const imgSrc = imgElement.src;

                    // Only process data URLs and blob URLs
                    if (imgSrc.startsWith("data:") || imgSrc.startsWith("blob:")) {
                        let imageData: Uint8Array;

                        if (imgSrc.startsWith("data:")) {
                            // Handle data URL
                            const base64Data = imgSrc.split(",")[1];
                            imageData = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
                        } else {
                            // Handle blob URL
                            const response = await fetch(imgSrc);
                            const blob = await response.blob();
                            const arrayBuffer = await blob.arrayBuffer();
                            imageData = new Uint8Array(arrayBuffer);
                        }

                        // Get image dimensions
                        const imgWidth = imgElement.naturalWidth || imgElement.width || 500;
                        const imgHeight = imgElement.naturalHeight || imgElement.height || 300;

                        // Calculate scaled dimensions (max 600x400)
                        const maxWidth = 600;
                        const maxHeight = 400;
                        let finalWidth = imgWidth;
                        let finalHeight = imgHeight;

                        if (imgWidth > maxWidth || imgHeight > maxHeight) {
                            const aspectRatio = imgWidth / imgHeight;
                            if (imgWidth > imgHeight) {
                                finalWidth = maxWidth;
                                finalHeight = maxWidth / aspectRatio;
                            } else {
                                finalHeight = maxHeight;
                                finalWidth = maxHeight * aspectRatio;
                            }
                        }

                        results.push(
                            new Paragraph({
                                children: [
                                    new ImageRun({
                                        type: "png",
                                        data: imageData,
                                        transformation: {
                                            width: Math.round(finalWidth),
                                            height: Math.round(finalHeight)
                                        }
                                    } as any)
                                ],
                                spacing: { before: 200, after: 200 }
                            })
                        );
                    } else {
                        // Skip external URLs
                        console.warn("External image URL skipped:", imgSrc);
                        results.push(
                            new Paragraph({
                                children: [new TextRun({ text: "[Image: " + (imgElement.alt || "External Image") + "]" })],
                                spacing: { before: 200, after: 200 }
                            })
                        );
                    }
                } catch (error) {
                    console.error("Failed to embed image:", error);
                    results.push(
                        new Paragraph({
                            children: [new TextRun({ text: "[Image]" })],
                            spacing: { before: 200, after: 200 }
                        })
                    );
                }
                break;

            default:
                // For other elements, process children or text content
                if (element.children.length > 0) {
                    for (const child of Array.from(element.children)) {
                        const childResults = await processElement(child);
                        results.push(...childResults);
                    }
                } else if (element.textContent?.trim()) {
                    results.push(
                        new Paragraph({
                            children: [new TextRun({ text: element.textContent })]
                        })
                    );
                }
                break;
        }

        return results;
    };

    // Helper function to split text with emojis and create appropriate TextRuns
    const createTextRunsWithEmojis = (text: string, formatting: any = {}): TextRun[] => {
        const runs: TextRun[] = [];

        // Simple emoji detection - matches most common emoji ranges
        // Uses a simpler pattern to avoid regex complexity and infinite loops
        const emojiRegex = /([\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])/gu;

        const parts = text.split(emojiRegex);

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (!part) continue; // Skip empty parts

            // Check if this part is an emoji (matched by the capturing group)
            const isEmoji = emojiRegex.test(part);
            // Reset regex after test
            emojiRegex.lastIndex = 0;

            if (isEmoji) {
                // For emojis, use Segoe UI Emoji font which properly supports colored glyphs
                // and don't apply any color formatting to preserve native emoji colors
                runs.push(new TextRun({
                    text: part,
                    font: {
                        name: "Segoe UI Emoji",
                        hint: "eastAsia"
                    }
                    // No color or other formatting - let emojis render naturally
                }));
            } else {
                // For regular text, apply the formatting
                runs.push(new TextRun({ text: part, ...formatting }));
            }
        }

        // If no runs created, return text with formatting
        if (runs.length === 0 && text) {
            runs.push(new TextRun({ text, ...formatting }));
        }

        return runs;
    };

    // Get heading formatting (size, bold, color) based on heading level
    const getHeadingFormatting = (tagName: string): any => {
        const formatting: any = {
            bold: true,
            color: "2E75B5" // Word's default heading blue color
        };

        switch (tagName) {
            case "h1":
                formatting.size = 32; // 16pt
                break;
            case "h2":
                formatting.size = 26; // 13pt
                break;
            case "h3":
                formatting.size = 24; // 12pt
                break;
            case "h4":
                formatting.size = 22; // 11pt
                break;
            case "h5":
                formatting.size = 20; // 10pt
                break;
            case "h6":
                formatting.size = 18; // 9pt
                break;
            default:
                formatting.size = 24;
        }

        return formatting;
    };

    // Process heading elements with special formatting that preserves emoji colors
    const processHeadingElements = (element: Element, tagName: string): TextRun[] => {
        const runs: TextRun[] = [];
        const headingFormatting = getHeadingFormatting(tagName);

        const processNode = (node: Node): void => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent || "";
                if (text) {
                    // Apply heading formatting to text, but emojis will ignore color
                    runs.push(...createTextRunsWithEmojis(text, headingFormatting));
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const childTagName = el.tagName.toLowerCase();
                const text = el.textContent || "";

                if (!text) return;

                const formatting: any = { ...headingFormatting };

                // Check for additional formatting
                if (childTagName === "strong" || childTagName === "b") formatting.bold = true;
                if (childTagName === "em" || childTagName === "i") formatting.italics = true;
                if (childTagName === "u") formatting.underline = {};
                if (childTagName === "s" || childTagName === "strike") formatting.strike = true;

                // Override color if explicitly set
                if (el.style.color) {
                    formatting.color = el.style.color.replace("#", "");
                }

                runs.push(...createTextRunsWithEmojis(text, formatting));
            }
        };

        element.childNodes.forEach(processNode);

        return runs.length > 0 ? runs : [new TextRun({ text: " " })];
    };

    // Process inline text formatting
    const processInlineElements = (element: Element): TextRun[] => {
        const runs: TextRun[] = [];

        const processNode = (node: Node): void => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent || "";
                if (text) {
                    // Use helper function to handle emojis
                    runs.push(...createTextRunsWithEmojis(text));
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const tagName = el.tagName.toLowerCase();
                const text = el.textContent || "";

                if (!text) return;

                const formatting: any = {};

                // Check for formatting
                if (tagName === "strong" || tagName === "b") formatting.bold = true;
                if (tagName === "em" || tagName === "i") formatting.italics = true;
                if (tagName === "u") formatting.underline = {};
                if (tagName === "s" || tagName === "strike") formatting.strike = true;
                if (tagName === "code") {
                    formatting.font = "Courier New";
                    formatting.shading = { fill: "F4F4F4" };
                }

                // Check for color
                if (el.style.color) {
                    formatting.color = el.style.color.replace("#", "");
                }

                // If this is a formatted element, create runs with emoji handling
                if (Object.keys(formatting).length > 0) {
                    runs.push(...createTextRunsWithEmojis(text, formatting));
                } else {
                    // Otherwise, process children
                    el.childNodes.forEach(processNode);
                }
            }
        };

        element.childNodes.forEach(processNode);

        // Return at least one run to avoid empty paragraphs
        return runs.length > 0 ? runs : [new TextRun({ text: " " })];
    };

    // Process all body children
    for (const element of Array.from(doc.body.children)) {
        const elementResults = await processElement(element);
        children.push(...elementResults);
    }

    // Ensure we have at least one paragraph
    if (children.length === 0) {
        children.push(new Paragraph({ children: [new TextRun({ text: " " })] }));
    }

    // Use the headingTracker we built while processing HTML
    console.log(`[Word Export] Total children: ${children.length}`);
    console.log(`[Word Export] Tracked headings: ${headingTracker.length}`);

    const hasHeadings = headingTracker.length > 0;
    console.log(`[Word Export] Has headings: ${hasHeadings}, count: ${headingTracker.length}`);

    // Add work product title page and TOC
    const tocElements: Paragraph[] = [];

    // Add title page if work product title is provided
    if (workProductTitle) {
        console.log("[Word Export] Adding title page");

        // Title
        const titleParagraph = new Paragraph({
            children: [
                new TextRun({
                    text: workProductTitle,
                    bold: true,
                    size: 56 // 28pt
                })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 2000, after: 800 }
        });

        tocElements.push(titleParagraph);

        // Page break after title
        tocElements.push(
            new Paragraph({
                children: [new PageBreak()]
            })
        );
    }

    // Table of Contents generation disabled per user request
    // Add Table of Contents if we have headings
    // if (hasHeadings) {
    //     console.log("[Word Export] Adding Table of Contents");

    //     // Add TOC title
    //     const tocTitle = new Paragraph({
    //         children: [
    //             new TextRun({
    //                 text: "Table of Contents",
    //                 bold: true,
    //                 size: 32 // 16pt
    //             })
    //         ],
    //         spacing: { before: 0, after: 400 }
    //     });

    //     tocElements.push(tocTitle);

    //     // Add automatic TOC field that Word will populate with page numbers
    //     console.log("[Word Export] Adding automatic TOC field (Word will populate page numbers)");
    //     const toc = new TableOfContents("Table of Contents", {
    //         hyperlink: true,
    //         headingStyleRange: "1-3" // Include H1, H2, H3
    //     });

    //     tocElements.push(toc as any);

    //     console.log(`[Word Export] TOC field added (${headingTracker.length} headings detected)`);

    //     // Add page break after TOC
    //     const pageBreak = new Paragraph({
    //         children: [new PageBreak()],
    //         spacing: { before: 400 }
    //     });

    //     tocElements.push(pageBreak);
    //     console.log(`[Word Export] TOC complete with ${headingTracker.length} headings (total elements: ${tocElements.length})`);
    // } else {
    //     console.log("[Word Export] No headings found, skipping TOC");
    // }
    console.log("[Word Export] Table of Contents generation is disabled");

    // Insert all TOC elements at the beginning
    if (tocElements.length > 0) {
        console.log(`[Word Export] Inserting ${tocElements.length} TOC elements at the beginning of document`);
        children.unshift(...tocElements);
        console.log(`[Word Export] Document now has ${children.length} total elements (including TOC)`);
    } else {
        console.log(`[Word Export] No TOC elements to insert`);
    }

    // Auto-detect regional paper size preference
    const paperSize = getRegionalPaperSize();
    console.log(`[Word Export] Detected locale: ${navigator.language}`);
    console.log(`[Word Export] Selected paper size: ${paperSize.name}`);
    console.log(`[Word Export] Dimensions: ${paperSize.width} x ${paperSize.height} twips`);

    // Create header with work product title (if provided)
    const header = workProductTitle
        ? new Header({
              children: [
                  new Paragraph({
                      children: [
                          new TextRun({
                              text: workProductTitle,
                              size: 18, // 9pt
                              color: "666666"
                          })
                      ],
                      alignment: AlignmentType.LEFT,
                      spacing: { after: 100 }
                  })
              ]
          })
        : undefined;

    // Create footer with page numbers
    const footer = new Footer({
        children: [
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Page ",
                        size: 18, // 9pt
                        color: "666666"
                    }),
                    new TextRun({
                        children: [PageNumber.CURRENT],
                        size: 18,
                        color: "666666"
                    }),
                    new TextRun({
                        text: " of ",
                        size: 18,
                        color: "666666"
                    }),
                    new TextRun({
                        children: [PageNumber.TOTAL_PAGES],
                        size: 18,
                        color: "666666"
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 100 }
            })
        ]
    });

    console.log(`[Word Export] Adding ${header ? "header and" : ""} footer to document`);

    // Log the exact page dimensions being applied
    console.log(`[Word Export] Creating Document with page dimensions:`);
    console.log(`[Word Export]   - width: ${paperSize.width} twips (${(paperSize.width / 1440).toFixed(2)} inches)`);
    console.log(`[Word Export]   - height: ${paperSize.height} twips (${(paperSize.height / 1440).toFixed(2)} inches)`);
    console.log(`[Word Export]   - width type: ${typeof paperSize.width}`);
    console.log(`[Word Export]   - height type: ${typeof paperSize.height}`);

    // Ensure values are integers (no floating point issues)
    const pageWidth = Math.floor(paperSize.width);
    const pageHeight = Math.floor(paperSize.height);

    console.log(`[Word Export] Final page dimensions: ${pageWidth} x ${pageHeight} twips`);

    return new Document({
        sections: [
            {
                properties: {
                    page: {
                        // Auto-detected paper size based on user's regional settings
                        // North America (US, CA, MX): Letter (8.5" × 11")
                        // Rest of world: A4 (210mm × 297mm)
                        size: {
                            width: pageWidth,
                            height: pageHeight,
                            orientation: PageOrientation.PORTRAIT
                        },
                        margin: {
                            top: 720, // 0.5 inch (1.27 cm)
                            right: 720, // 0.5 inch (1.27 cm)
                            bottom: 720, // 0.5 inch (1.27 cm)
                            left: 720 // 0.5 inch (1.27 cm)
                        }
                    }
                },
                headers: header ? { default: header } : undefined,
                footers: {
                    default: footer
                },
                children: children
            }
        ],
        numbering: {
            config: [
                {
                    reference: "default-numbering",
                    levels: [
                        {
                            level: 0,
                            format: "decimal",
                            text: "%1.",
                            alignment: AlignmentType.LEFT,
                            style: {
                                paragraph: {
                                    indent: { left: 720, hanging: 360 }
                                }
                            }
                        }
                    ]
                }
            ]
        }
    });
}

/**
 * Get alignment from element style
 */
function getAlignment(element: Element): (typeof AlignmentType)[keyof typeof AlignmentType] {
    const htmlElement = element as HTMLElement;
    const textAlign = htmlElement.style.textAlign;

    switch (textAlign) {
        case "center":
            return AlignmentType.CENTER;
        case "right":
            return AlignmentType.RIGHT;
        case "justify":
            return AlignmentType.JUSTIFIED;
        default:
            return AlignmentType.LEFT;
    }
}
